import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Autocomplete,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Button,
  Grid,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CloseIcon from "@mui/icons-material/Close";
import Male from "../../assets/images/male.png";
import { Country, State } from "country-state-city";
import { useSelector } from "react-redux";
import Webcam from "react-webcam";
import PhoneInput, {
  isValidPhoneNumber,
  getCountryCallingCode,
  parsePhoneNumber,
} from "react-phone-number-input";
import "react-phone-number-input/style.css";
import phoneLabels from "react-phone-number-input/locale/en.json";
import PincodeAutocomplete from "./components/PincodeAutocomplete";
import PhoneCountryAutocomplete from "./components/PhoneCountryAutocomplete";
import MDSnackbar from "components/MDSnackbar";
import { CITIES } from "../../data/cities";
import { LANGUAGES } from "../../data/languages";
import { fetchMasterData } from "../../services/masterDataService";
import AddMasterDataDialog from "components/AddMasterDataDialog";
import { usePincodeAutofill } from "../../hooks/usePincodeAutofill";
import EducationFields from "../../components/FormComponents/EducationFields";
import WorkFields from "../../components/FormComponents/WorkFields";
import IDCardFields from "../../components/FormComponents/IDCardFields";
import BankDetailsFields from "../../components/FormComponents/BankDetailsFields";
import SocialLinksFields from "../../components/FormComponents/SocialLinksFields";
import AddressFields from "../../components/FormComponents/AddressFields";
import { fetchWorkDetailsByParent, syncWorkEntries } from "../../services/workService";
import { fetchEducationDetailsByParent, syncEducationEntries } from "../../services/educationService";
import { fetchIDCardDetailsByParent, syncIDCardEntries } from "../../services/idCardService";
import { fetchBankDetailsByParent, syncBankDetailsEntries } from "../../services/bankDetailsService";
import { fetchSocialLinksByParent, syncSocialLinksEntries } from "../../services/socialLinksService";
import { callFamilyTreeApi } from "../../services/familyTreeService";
import {
  schoolBoards as boards,
  courses,
  degreeTypes,
  institutes,
} from "../../data/educationData";

const MIN_PHONE_DIGITS = 4;
const hasUsablePhoneValue = (value) => {
  if (!value) return false;
  const digits = value.replace(/\D/g, "");
  return digits.length >= MIN_PHONE_DIGITS;
};

const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15MB limit

// Helper function to decode HTML entities
const decodeHTMLEntities = (text) => {
  if (!text) return text;
  const textArea = document.createElement('textarea');
  textArea.innerHTML = text;
  return textArea.value;
};

const FamilyMemberDialog = ({ 
  mode = 'add', // 'add' or 'edit'
  memberData = null, // Member data for edit mode
  onAddMember, 
  onUpdateMember,
  generateID, 
  familyMembers, 
  onClose,
  familyGroupId: propFamilyGroupId = ''
}) => {
  const navigate = useNavigate();
  const sessionId = useSelector((state) => state.session.sessionId);
  const [castes, setCastes] = useState([]);
  const [religions, setReligions] = useState([]);
  const [relationships, setRelationships] = useState([]);

  useEffect(() => {
    if (!sessionId) return;
    const loadMasterData = async () => {
      try {
        const data = await fetchMasterData(sessionId);
        setCastes(data.castes);
        setReligions(data.religions);
        setRelationships(data.relationships);
      } catch (error) {
        console.error("Failed to load master data:", error);
        setCastes([]);
        setReligions([]);
        setRelationships([]);
      }
    };
    loadMasterData();
  }, [sessionId]);
  const [addDialog, setAddDialog] = useState({ open: false, category: null, initialValue: "" });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, data: null });
  const [relationshipConfirmed, setRelationshipConfirmed] = useState(false);
  const [allStates, setAllStates] = useState([]);
  const [allCountries, setAllCountries] = useState([]);

  useEffect(() => {
    const countries = Country.getAllCountries();
    const states = State.getAllStates();
    setAllCountries(countries);
    setAllStates(states);
  }, []);

  // Initialize form state based on mode
  const getInitialState = () => {
    const defaultState = {
      name: "",
      mobile: "",
      phoneCountry: "IN",
      email: "",
      // village/town and taluk fields (match edit profile)
      villageTown_c: "",
      taluk_c: "",
      district_c: "",
      relation: "",
      aadhar: "",
      wifeId: "",
      photo: null,
      photoFile: null,
      caste: "",
      religion: "",
      relationshipType: "",
      selectedPersonId: "",
      instagram: "",
      youtube: "",
      facebook: "",
      threads: "",
      linkedin: "",
      instagram_c: "",
      youtube_c: "",
      facebook_c: "",
      threads_c: "",
      linkedin_c: "",
      // Personal Information
      address1_c: "",
      address2_c: "",
      age_c: "",
      alivestatus_c: "Alive",
      birthplace_c: "",
      bloodgroup_c: "",
      city_c: "",
      country_c: "",
      district_c: "",
      dateofdeath_c: "",
      deathplace_c: "",
      deathreason_c: "",
      deathaddress_c: "",
      deatharea_c: "",
      deathpincode_c: "",
      deathcity_c: "",
      deathstate_c: null,
      deathcountry_c: null,
      disability_c: "",
      dob_c: "",
      gender_c: "",
      languagesknown_c: "",
      maritalstatus_c: "",
      marriagedate_c: "",
      marriageplace_c: "",
      mothertongue_c: "",
      economical_status_c: "",
      pincode_c: "",
      state_c: "",
  // additional location fields
  villageTown_c: "",
  taluk_c: "",
  district_c: "",
      // Education Details
      education1_c: "",
      education2_c: "",
      education3_c: "",
      education4_c: "",
      education5_c: "",
    educationimage1: "",
    educationimage2: "",
    educationimage3: "",
    educationimage4: "",
    educationimage5: "",
      educationtype1_c: "Regular",
      educationtype2_c: "",
      educationtype3_c: "",
      educationtype4_c: "",
      educationtype5_c: "",
      institute1_c: "",
      institute2_c: "",
      institute3_c: "",
      institute4_c: "",
      institute5_c: "",
      yearofpassing1_c: "",
      yearofpassing2_c: "",
      yearofpassing3_c: "",
      yearofpassing4_c: "",
      yearofpassing5_c: "",
      // Work Details - Multiple sections (1-5)
      companyname1: "",
      companyname2: "",
      companyname3: "",
      companyname4: "",
      companyname5: "",
      designation1: "",
      designation2: "",
      designation3: "",
      designation4: "",
      designation5: "",
      department1: "",
      department2: "",
      department3: "",
      department4: "",
      department5: "",
      employmenttype1: "",
      employmenttype2: "",
      employmenttype3: "",
      employmenttype4: "",
      employmenttype5: "",
      sector1: "",
      sector2: "",
      sector3: "",
      sector4: "",
      sector5: "",
      description1: "",
      description2: "",
      description3: "",
      description4: "",
      description5: "",
      startdate1: "",
      startdate2: "",
      startdate3: "",
      startdate4: "",
      startdate5: "",
      enddate1: "",
      enddate2: "",
      enddate3: "",
      enddate4: "",
      enddate5: "",
      currentlyworking1: false,
      currentlyworking2: false,
      currentlyworking3: false,
      currentlyworking4: false,
      currentlyworking5: false,
      address1: "",
      address2: "",
      address3: "",
      address4: "",
      address5: "",
      country1: "",
      country2: "",
      country3: "",
      country4: "",
      country5: "",
      state1: "",
      state2: "",
      state3: "",
      state4: "",
      state5: "",
      city1: "",
      city2: "",
      city3: "",
      city4: "",
      city5: "",
      pincode1: "",
      pincode2: "",
      pincode3: "",
      pincode4: "",
      pincode5: "",
      // Legacy work fields (keeping for backward compatibility)
      company_c: "",
      employmenttype_c: "",
      occupation_c: "",
      worklocation_c: "",
    };

    // In edit mode, pre-populate with memberData
    if (mode === 'edit' && memberData) {
      // Populate relationshipType from available fields
      // Use relationship_type_c first, then fallback to relationship or relation
      const rawRelation = memberData.relationship_type_c || memberData.relationship || memberData.relation || "";
      
      // Map the relation value to our dropdown options (Parent, Spouse, Child)
      let relationshipType = "";
      if (rawRelation) {
        const normalized = rawRelation.toLowerCase();
        if (normalized.includes("child") || normalized.includes("son") || normalized.includes("daughter")) {
          relationshipType = "Child";
        } else if (normalized.includes("parent") || normalized.includes("father") || normalized.includes("mother")) {
          relationshipType = "Parent";
        } else if (normalized.includes("spouse") || normalized.includes("husband") || normalized.includes("wife")) {
          relationshipType = "Spouse";
        }
      }
      
      // Populate selectedPersonId based on relationshipType
      let selectedPersonId = "";
      if (relationshipType === "Parent" || relationshipType === "Child") {
        // For Parent or Child, get from parent-related fields
        selectedPersonId = memberData.parentid_c || "";
      } else if (relationshipType === "Spouse") {
        // For Spouse, get from spouse-related fields
        selectedPersonId = memberData.spouseid_c || "";
      }
      
      return {
        ...defaultState,
        ...memberData,
        relationshipType,
        selectedPersonId,
        // Convert "Not specified" to empty string for caste and religion
        caste: (memberData.caste || "").toLowerCase() === "not specified" ? "" : (memberData.caste || ""),
        religion: (memberData.religion || "").toLowerCase() === "not specified" ? "" : (memberData.religion || ""),
        // Ensure state and country are objects, not strings
        state: memberData.state && typeof memberData.state === 'string' 
          ? allStates.find(s => s.name === memberData.state) || null
          : memberData.state || null,
        country: memberData.country && typeof memberData.country === 'string'
          ? allCountries.find(c => c.name === memberData.country) || null
          : memberData.country || null,
      };
    }

    return defaultState;
  };

  const [newMember, setNewMember] = useState(getInitialState());

  // Pincode autofill hook with custom field mapping for FamilyMemberDialog
  // Must be called AFTER setNewMember is defined
  const {
    handlePincodeChange: handlePincodeInput,
    loading: pincodeLoading,
    error: pincodeError,
    locationData,
    getVillageOptions,
  } = usePincodeAutofill(setNewMember, {
    pincode: 'pincode',
    city: 'city',
    state: 'state',
    country: 'country',
    district: 'district_c',
    taluk: 'taluk_c',
    village: 'villageTown_c',
  });

  // Get village options from pincode data
  const villageOptions = locationData?.allAreas || [];

  // Death pincode autofill hook with custom field mapping for death location
  const {
    handlePincodeChange: handleDeathPincodeInput,
    loading: deathPincodeLoading,
    error: deathPincodeError,
    locationData: deathLocationData,
  } = usePincodeAutofill(setNewMember, {
    pincode: 'deathpincode_c',
    city: 'deathcity_c',
    state: 'deathstate_c',
    country: 'deathcountry_c',
    area: 'deatharea_c',
  });

  // Get death area options from death pincode data
  const deathAreaOptions = deathLocationData?.allAreas || [];

  // Re-initialize form when memberData changes in edit mode
  useEffect(() => {
    if (mode === 'edit' && memberData) {
      setNewMember(getInitialState());
    }
  }, [mode, memberData, allStates, allCountries]);

  const [touchedFields, setTouchedFields] = useState({
    mobile: false,
    aadhar: false,
    selectedPerson: false,
  });

  // Track whether permanent address should mirror current address
  const [sameAsCurrent, setSameAsCurrent] = useState(false);

  // State to track visible education sections (initially show only Education 1)
  const [visibleEducationSections, setVisibleEducationSections] = useState(1);
  const [educationSectionError, setEducationSectionError] = useState("");
  const [educationFieldErrors, setEducationFieldErrors] = useState({});
  const maxEducationSections = 10;

  // State to track visible work sections (initially show only Work 1)
  const [visibleWorkSections, setVisibleWorkSections] = useState(1);
  const [workSectionError, setWorkSectionError] = useState("");
  const [workFieldErrors, setWorkFieldErrors] = useState({});
  const maxWorkSections = 5;

  // State to track visible ID card sections
  const [visibleIDCardSections, setVisibleIDCardSections] = useState(1);
  const [idCardSectionError, setIDCardSectionError] = useState("");
  const [idCardFieldErrors, setIDCardFieldErrors] = useState({});
  const maxIDCardSections = 10;

  // State to track visible bank details sections
  const [visibleBankSections, setVisibleBankSections] = useState(1);
  const [bankSectionError, setBankSectionError] = useState("");
  const [bankFieldErrors, setBankFieldErrors] = useState({});
  const maxBankSections = 10;

  // State to track visible social links sections
  const [visibleSocialLinksSections, setVisibleSocialLinksSections] = useState(1);
  const [socialLinksSectionError, setSocialLinksSectionError] = useState("");
  const [socialLinksFieldErrors, setSocialLinksFieldErrors] = useState({});
  const maxSocialLinksSections = 10;

  // Get logged-in user ID from Redux (needed for "Myself" relation)
  const user_id = useSelector((state) =>
    state.session.setUserId ? state.session.setUserId.value : null
  );

  // Fetch work details from work_work module when opening in edit mode
  useEffect(() => {
    const fetchWorkDetails = async () => {
      if (mode === 'edit' && memberData && sessionId) {
        try {
          // For "Myself", use the logged-in user's ID instead of the family member record ID
          let parentId = memberData.id || memberData.record_id;
          const isMySelf = newMember.relation?.toLowerCase() === "myself" || memberData.relation?.toLowerCase() === "myself";
          
          if (isMySelf && (!parentId || parentId === "0" || parentId === 0)) {
            parentId = user_id;
          }
          
          
          const workEntries = await fetchWorkDetailsByParent(sessionId, parentId);
          
          if (workEntries && workEntries.length > 0) {
            // Map work entries to form fields
            const workData = {};
            workEntries.forEach((entry, index) => {
              const section = index + 1;
              if (section <= maxWorkSections) {
                workData[`companyname${section}`] = entry.companyname || "";
                workData[`designation${section}`] = entry.designation || "";
                workData[`department${section}`] = entry.department || "";
                workData[`employmenttype${section}`] = entry.employmenttype || "";
                workData[`sector${section}`] = entry.sector || "";
                workData[`description${section}`] = entry.description || "";
                workData[`startdate${section}`] = entry.startdate || "";
                workData[`enddate${section}`] = entry.enddate || "";
                workData[`currentlyworking${section}`] = entry.currentlyworking === "1" || entry.currentlyworking === true;
                workData[`address${section}`] = entry.address || "";
                workData[`country${section}`] = entry.country || "";
                workData[`state${section}`] = entry.state || "";
                workData[`city${section}`] = entry.city || "";
                workData[`pincode${section}`] = entry.pincode || "";
              }
            });
            
            
            // Update form state with work data
            setNewMember((prev) => ({
              ...prev,
              ...workData,
            }));
            
            // Set visible sections based on entries count
            setVisibleWorkSections(Math.min(workEntries.length, maxWorkSections));
          } else {
          }
        } catch (error) {
          console.error('❌ Error fetching work details:', error);
        }
      }
    };
    
    fetchWorkDetails();
  }, [mode, memberData, sessionId, maxWorkSections]);

  // Fetch education details from education_education module when opening in edit mode
  useEffect(() => {
    const fetchEducationDetails = async () => {
      if (mode === 'edit' && memberData && sessionId) {
        try {
          // For "Myself", use the logged-in user's ID instead of the family member record ID
          let parentId = memberData.id || memberData.record_id;
          const isMySelf = newMember.relation?.toLowerCase() === "myself" || memberData.relation?.toLowerCase() === "myself";
          
          if (isMySelf && (!parentId || parentId === "0" || parentId === 0)) {
            parentId = user_id;
          }
          
          const educationEntries = await fetchEducationDetailsByParent(sessionId, parentId);

          if (educationEntries && educationEntries.length > 0) {
            // Map education entries to form fields
            const educationData = {};
            educationEntries.forEach((entry, index) => {
              const section = index + 1;

              if (section <= maxEducationSections) {
                // Map to the FamilyMemberDialog field names (with _c suffix)
                educationData[`education${section}_c`] = entry.qualification || entry.education || "";
                educationData[`board${section}_c`] = entry.board || "";
                educationData[`course${section}_c`] = entry.course || "";
                educationData[`educationtype${section}_c`] = entry.educationtype || "Regular";
                educationData[`institute${section}_c`] = entry.instituteschool || entry.institute || "";
                educationData[`institutecountry${section}_c`] = entry.institutecountry || "";
                educationData[`institutestate${section}_c`] = entry.institutestate || "";
                educationData[`institutecity${section}_c`] = entry.instituecity || "";
                educationData[`institutepincode${section}_c`] = entry.institutepincode || "";
                educationData[`startyear${section}_c`] = entry.startyear_c || "";
                educationData[`currentlystudying${section}_c`] = entry.currentlystudying_c === "1" || entry.currentlystudying_c === true;
                educationData[`endyear${section}_c`] = entry.endyear_c || "";
                educationData[`grade${section}_c`] = entry.grade || "";
                educationData[`educationimage${section}`] = entry.image || "";
              }
            });

            // Update form state with education data
            setNewMember((prev) => ({
              ...prev,
              ...educationData,
            }));

            // Set visible sections based on entries count
            setVisibleEducationSections(Math.min(educationEntries.length, maxEducationSections));
          } else {
          }
        } catch (error) {
          console.error('❌ [EDUCATION FETCH] Error:', error);
          console.error('❌ [EDUCATION FETCH] Error details:', error.message, error.stack);
        }
      } else {
      }
    };
    
    fetchEducationDetails();
  }, [mode, memberData, sessionId, maxEducationSections]);

  // Fetch ID card details from id_idcards module when opening in edit mode
  useEffect(() => {
    const fetchIDCardDetails = async () => {
      if (mode === 'edit' && memberData && sessionId) {
        try {
          // For "Myself", use the logged-in user's ID instead of the family member record ID
          let parentId = memberData.id || memberData.record_id;
          const isMySelf = newMember.relation?.toLowerCase() === "myself" || memberData.relation?.toLowerCase() === "myself";
          
          if (isMySelf && (!parentId || parentId === "0" || parentId === 0)) {
            parentId = user_id;
            }
          
         
          const idCardEntries = await fetchIDCardDetailsByParent(sessionId, parentId);
         
          if (idCardEntries && idCardEntries.length > 0) {
            // Map ID card entries to form fields
            const idCardData = {};
            idCardEntries.forEach((entry, index) => {
              const section = index + 1;
             
              if (section <= maxIDCardSections) {
                // Map to the FamilyMemberDialog field names
                idCardData[`idCardEntryId${section}`] = entry.id || "";
                idCardData[`idtype${section}`] = entry.idtype || "";
                idCardData[`idcardname${section}`] = entry.idcardname || entry.name || "";
                idCardData[`idnumber${section}`] = entry.idnumber || "";
                idCardData[`issuedate${section}`] = entry.issuedate || "";
                idCardData[`expirydate${section}`] = entry.expirydate || "";
                idCardData[`idcarddescription${section}`] = entry.idcarddescription || entry.description || "";
                
               
              }
            });
           
            // Update form state with ID card data
            setNewMember((prev) => ({
              ...prev,
              ...idCardData,
            }));
            
            // Set visible sections based on entries count
            setVisibleIDCardSections(Math.min(idCardEntries.length, maxIDCardSections));
            
           
          } else {
          }
        } catch (error) {
          console.error('❌ [ID CARD FETCH] Error:', error);
          console.error('❌ [ID CARD FETCH] Error details:', error.message, error.stack);
        }
      } else {
      }
    };
    
    fetchIDCardDetails();
  }, [mode, memberData, sessionId, maxIDCardSections, user_id]);

  // Fetch bank details from bank_bankdetails module when opening in edit mode
  useEffect(() => {
    const fetchBankDetails = async () => {
      if (mode === 'edit' && memberData && sessionId) {
        try {
          // For "Myself", use the logged-in user's ID instead of the family member record ID
          let parentId = memberData.id || memberData.record_id;
          const isMySelf = newMember.relation?.toLowerCase() === "myself" || memberData.relation?.toLowerCase() === "myself";
          
          if (isMySelf && (!parentId || parentId === "0" || parentId === 0)) {
            parentId = user_id;
            }
          
        
          const bankEntries = await fetchBankDetailsByParent(sessionId, parentId);
         
          if (bankEntries && bankEntries.length > 0) {
            // Map bank entries to form fields
            const bankData = {};
            bankEntries.forEach((entry, index) => {
              const section = index + 1;
              
              if (section <= maxBankSections) {
                // Map to the FamilyMemberDialog field names
                bankData[`bankEntryId${section}`] = entry.id || "";
                bankData[`accountholdername${section}`] = entry.accountholdername || "";
                bankData[`accountnumber${section}`] = entry.accountnumber || "";
                bankData[`ifsc${section}`] = entry.ifsc || "";
                bankData[`accounttype${section}`] = entry.accounttype || "";
                bankData[`bankname${section}`] = entry.bankname || "";
                bankData[`branch${section}`] = entry.branch || "";
                bankData[`bankcountry${section}`] = entry.bankcountry || "";
                bankData[`status${section}`] = entry.status || "Active";
                bankData[`bankdescription${section}`] = entry.bankdescription || entry.description || "";
                bankData[`image${section}`] = entry.image || "";
              
              }
            });
            
         
            // Update form state with bank data
            setNewMember((prev) => ({
              ...prev,
              ...bankData,
            }));
            
            // Set visible sections based on entries count
            setVisibleBankSections(Math.min(bankEntries.length, maxBankSections));
            
          } else {
          }
        } catch (error) {
          console.error('❌ [BANK FETCH] Error:', error);
          console.error('❌ [BANK FETCH] Error details:', error.message, error.stack);
        }
      } else {
      }
    };
    
    fetchBankDetails();
  }, [mode, memberData, sessionId, maxBankSections, user_id]);

  // Fetch social links from social_sociallinks module when opening in edit mode
  useEffect(() => {
    const fetchSocialLinks = async () => {
      if (mode === 'edit' && memberData && sessionId) {
        try {
          // For "Myself", use the logged-in user's ID instead of the family member record ID
          let parentId = memberData.id || memberData.record_id;
          const isMySelf = newMember.relation?.toLowerCase() === "myself" || memberData.relation?.toLowerCase() === "myself";
          
          if (isMySelf && (!parentId || parentId === "0" || parentId === 0)) {
            parentId = user_id;
          }
          const socialLinksEntries = await fetchSocialLinksByParent(sessionId, parentId);
          
          if (socialLinksEntries && socialLinksEntries.length > 0) {
            // Map social links entries to form fields
            const socialLinksData = {};
            socialLinksEntries.forEach((entry, index) => {
              const section = index + 1;
              
              if (section <= maxSocialLinksSections) {
                // Map to the FamilyMemberDialog field names
                // API returns 'type' and 'link', not 'socialtype' and 'sociallink'
                socialLinksData[`socialLinksEntryId${section}`] = entry.id || "";
                socialLinksData[`socialtype${section}`] = entry.type || "";
                socialLinksData[`sociallink${section}`] = entry.link || "";
                
               
              }
            });
           
            // Update form state with social links data
            setNewMember((prev) => ({
              ...prev,
              ...socialLinksData,
            }));
            
            // Set visible sections based on entries count
            setVisibleSocialLinksSections(Math.min(socialLinksEntries.length, maxSocialLinksSections));
            
          } else {
            
            // Check if there's data in old static fields that needs to be migrated
            const legacySocialLinks = [
              { type: 'Instagram', field: 'instagram_c' },
              { type: 'Facebook', field: 'facebook_c' },
              { type: 'YouTube', field: 'youtube_c' },
              { type: 'LinkedIn', field: 'linkedin_c' },
              { type: 'Threads', field: 'threads_c' },
            ];

            const socialLinksData = {};
            let sectionCount = 0;

            legacySocialLinks.forEach(({ type, field }) => {
              const value = memberData[field] || newMember[field];
              if (value && value.trim() !== '') {
                sectionCount++;
                socialLinksData[`socialtype${sectionCount}`] = type;
                socialLinksData[`sociallink${sectionCount}`] = value;
                 }
            });

            if (sectionCount > 0) {
            
              setVisibleSocialLinksSections(sectionCount);
            }
          }
        } catch (error) {
          console.error('❌ [SOCIAL LINKS FETCH] Error:', error);
          console.error('❌ [SOCIAL LINKS FETCH] Error details:', error.message, error.stack);
        }
      } else {
      }
    };
    
    fetchSocialLinks();
  }, [mode, memberData, sessionId, maxSocialLinksSections, user_id, newMember]);

  // Compute visible work sections when data loads (for edit mode)
  useEffect(() => {
    if (mode === 'edit' && memberData) {
      let maxFilledSection = 0;
      for (let i = 1; i <= maxWorkSections; i++) {
        const hasData = [
          newMember[`companyname${i}`],
          newMember[`designation${i}`],
          newMember[`employmenttype${i}`],
          newMember[`startdate${i}`],
        ].some((value) => {
          if (typeof value === "string") {
            return value.trim() !== "";
          }
          return value !== undefined && value !== null && value !== "";
        });

        if (hasData) {
          maxFilledSection = i;
        }
      }

      if (maxFilledSection > 0) {
        setVisibleWorkSections(maxFilledSection);
      }
    }
  }, [mode, memberData, newMember, maxWorkSections]);

  // Helper function to get required fields for an education section
  // Returns EducationFields field names for error display
  const getEducationRequiredFields = useCallback(
    (section) => [
      `qualification${section}`,
      `board${section}`,
      `educationtype${section}`,
      `instituteschool${section}`,
    ],
    []
  );

  // Map EducationFields field names to FamilyMemberDialog field names
  const mapEducationFieldNameToStorage = (fieldName) => {
    const match = fieldName.match(/(\w+?)(\d+)$/);
    if (!match) {
      return fieldName;
    }
    
    const [, base, section] = match;
    
    const fieldMap = {
      'qualification': `education${section}_c`,
      'board': `board${section}_c`,
      'educationtype': `educationtype${section}_c`,
      'instituteschool': `institute${section}_c`,
      'educationimage': `educationimage${section}`,
    };
    
    const result = fieldMap[base] || fieldName;
    return result;
  };

  // Check if primary education fields are filled for a section
  const isEducationSectionPrimaryFieldsFilled = (section) => {
    const requiredFields = getEducationRequiredFields(section);
    
    return requiredFields.every((fieldName) => {
      // Map to actual storage field name
      const storageFieldName = mapEducationFieldNameToStorage(fieldName);
      const value = newMember[storageFieldName];
      if (typeof value === "string") {
        return value.trim() !== "";
      }
      return value !== undefined && value !== null && value !== "";
    });
  };

  // Initialize education type with default value "Regular" for each visible section
  useEffect(() => {
    setNewMember((prev) => {
      const updated = { ...prev };
      let hasChanges = false;
      
      // Set default education type for all visible sections if not already set
      for (let i = 1; i <= visibleEducationSections; i++) {
        const fieldName = `educationtype${i}_c`;
        if (!updated[fieldName] || updated[fieldName].trim() === "") {
          updated[fieldName] = "Regular";
          hasChanges = true;
        }
      }
      
      return hasChanges ? updated : prev;
    });
  }, [visibleEducationSections]);

  // Auto-clear education section errors when fields are filled
  useEffect(() => {
    if (educationSectionError) {
      const currentSection = visibleEducationSections;
      if (isEducationSectionPrimaryFieldsFilled(currentSection)) {
        setEducationSectionError("");
        const fieldsToClear = getEducationRequiredFields(currentSection);
        setEducationFieldErrors((prev) => {
          const next = { ...prev };
          fieldsToClear.forEach((fieldName) => {
            delete next[fieldName];
          });
          return next;
        });
      }
    }
  }, [educationSectionError, visibleEducationSections, newMember, getEducationRequiredFields]);

  const inputRefs = useRef([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const handleArrowNavigation = (e) => {
      const index = inputRefs.current.findIndex((ref) => ref === document.activeElement);
      if (index === -1) return;

      let nextIndex = index;

      if (e.key === "ArrowDown" || e.key === "ArrowRight") {
        nextIndex = Math.min(inputRefs.current.length - 1, index + 1);
      } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
        nextIndex = Math.max(0, index - 1);
      }

      if (nextIndex !== index && inputRefs.current[nextIndex]) {
        inputRefs.current[nextIndex].focus();
        e.preventDefault(); // stop cursor movement inside input
      }
    };

    document.addEventListener("keydown", handleArrowNavigation);
    return () => document.removeEventListener("keydown", handleArrowNavigation);
  }, []);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [cameraDialogOpen, setCameraDialogOpen] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const webcamRef = useRef(null);
  const [facingMode, setFacingMode] = useState('user');
  const [cameraError, setCameraError] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [webcamKey, setWebcamKey] = useState(-1);
  const [cameraRetryCount, setCameraRetryCount] = useState(0);
  const [cameraMessage, setCameraMessage] = useState(null);
  const [videoDevices, setVideoDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const triedDevicesRef = useRef(new Set());
  const [loosenConstraints, setLoosenConstraints] = useState(false);
  const [forceDefaultDevice, setForceDefaultDevice] = useState(false);
  const [useBareConstraints, setUseBareConstraints] = useState(false);

  const MAX_CAMERA_RETRIES = 4;

  const resetTriedDevices = useCallback(() => {
    triedDevicesRef.current = new Set();
  }, []);

  const fetchVideoDevices = useCallback(async () => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices) {
      throw new Error("Camera API not supported in this browser.");
    }

    const enumerateVideoInputs = async () => {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.filter((device) => device.kind === "videoinput");
    };

    let devices = await enumerateVideoInputs();

    if (!devices.length) {
      throw new Error("No camera devices found.");
    }

    if (devices.every((device) => !device.label)) {
      let tempStream;
      try {
        tempStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        });
        devices = await enumerateVideoInputs();
      } catch (permissionError) {
        console.error(
          "Camera request failed while enumerating devices:",
          permissionError
        );

        const errorName = permissionError?.name;

        if (errorName === "NotAllowedError" || errorName === "PermissionDeniedError") {
          throw new Error("Camera permission was denied. Please allow camera access and try again.");
        }

        if (errorName === "NotReadableError") {
          throw new Error(
            "Your camera appears to be in use by another application. Please close any other app that might be using the camera and try again."
          );
        }

        if (errorName === "OverconstrainedError") {
          throw new Error(
            "No camera could satisfy the requested settings. Try lowering the camera quality or switching devices."
          );
        }

        if (errorName === "AbortError") {
          throw new Error(
            "The camera took too long to start. Please try again or switch to a different device."
          );
        }

        if (permissionError?.message) {
          throw new Error(permissionError.message);
        }

        throw permissionError;
      } finally {
        if (tempStream) {
          tempStream.getTracks().forEach((track) => track.stop());
        }
      }

      devices = devices.filter((device) => device.kind === "videoinput");

      if (!devices.length) {
        throw new Error("No camera devices found.");
      }
    }

    return devices;
  }, []);

  const selectDeviceIdForMode = useCallback((devices, mode, { preferUntried = false } = {}) => {
    if (!devices?.length) {
      return null;
    }

    const labelPattern =
      mode === "user"
        ? /(front|user|face|webcam)/i
        : /(back|rear|environment|world)/i;

    const consideredDevices = preferUntried
      ? devices.filter((device) => !triedDevicesRef.current.has(device.deviceId))
      : devices;

    const labeledMatch = consideredDevices.find((device) => labelPattern.test(device.label));

    if (labeledMatch) {
      return labeledMatch.deviceId;
    }

    const untriedFallback = devices.find((device) => !triedDevicesRef.current.has(device.deviceId));
    if (untriedFallback) {
      return untriedFallback.deviceId;
    }

    return consideredDevices[0]?.deviceId || devices[0]?.deviceId || null;
  }, []);

  const setActiveDevice = useCallback((deviceId) => {
    if (!deviceId) {
      return;
    }
    triedDevicesRef.current.add(deviceId);
    setSelectedDeviceId(deviceId);
  }, []);

  const stopCameraStream = useCallback(() => {
    const stream = webcamRef.current?.stream || webcamRef.current?.video?.srcObject;
    if (stream) {
      const tracks = stream.getTracks ? stream.getTracks() : [];
      tracks.forEach((track) => track.stop());
    }
    if (webcamRef.current) {
      if (webcamRef.current.video) {
        webcamRef.current.video.srcObject = null;
      }
      webcamRef.current.stream = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      stopCameraStream();
    };
  }, [stopCameraStream]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "aadhar") {
      // Only allow digits (no alphabets or special characters)
      if (/^\d*$/.test(value)) {
        // Set exact length restrictions: 12 for aadhar
        const maxLength = 12;
        if (value.length <= maxLength) {
          setNewMember((prev) => {
            const updated = { ...prev, [name]: value };
            // If sameAsCurrent is enabled, mirror current_ fields to permanent_
            if (sameAsCurrent && name.startsWith("current_")) {
              const permName = name.replace("current_", "permanent_");
              updated[permName] = value;
            }
            return updated;
          });
        }
      }
    } else {
      setNewMember((prev) => {
        const updated = { ...prev, [name]: value };
        // When user has opted to keep permanent same as current, mirror updates
        if (sameAsCurrent && name.startsWith("current_")) {
          const permName = name.replace("current_", "permanent_");
          updated[permName] = value;
        }
        return updated;
      });
    }
  };

  // Adapter for AddressFields: map AddressFields names (current_*/permanent_*) to
  // the form's canonical keys so values persist and show up in the form.
  const handleAddressFieldChange = (e) => {
    const { name, value } = e.target;

    setNewMember((prev) => {
      const updated = { ...prev, [name]: value };

      // Map current address fields into canonical member fields used elsewhere
      if (name.startsWith("current_")) {
        if (name === "current_address1") {
          updated.address = value;
          updated.address1_c = value;
        }
        if (name === "current_street1") {
          updated.address2_c = value;
        }
        if (name === "current_pincode1") {
          updated.pincode = value;
          updated.pincode_c = value;
        }
        if (name === "current_village1") {
          updated.city = value;
          updated.villageTown_c = value;
        }
        if (name === "current_taluk1") {
          updated.taluk_c = value;
        }
        if (name === "current_district1") {
          updated.district_c = value;
        }
        if (name === "current_state1") {
          updated.state = value;
        }
        if (name === "current_country1") {
          updated.country = value;
        }

        // If user selected 'Same as Current', mirror to permanent fields as well
        if (sameAsCurrent) {
          const permName = name.replace("current_", "permanent_");
          updated[permName] = value;
          // Also map to some permanent canonical fields
          if (permName === "permanent_address1") {
            updated.permanent_address1 = value;
            updated.permanent_street1 = prev.current_street1 || prev.permanent_street1 || "";
          }
          if (permName === "permanent_pincode1") {
            updated.permanent_pincode1 = value;
          }
        }
      }

      // Map permanent address fields to their own keys (do not overwrite primary address)
      if (name.startsWith("permanent_")) {
        if (name === "permanent_address1") {
          updated.permanent_address1 = value;
        }
        if (name === "permanent_street1") {
          updated.permanent_street1 = value;
        }
        if (name === "permanent_pincode1") {
          updated.permanent_pincode1 = value;
        }
        if (name === "permanent_village1") {
          updated.permanent_village1 = value;
        }
        if (name === "permanent_taluk1") {
          updated.permanent_taluk1 = value;
        }
        if (name === "permanent_district1") {
          updated.permanent_district1 = value;
        }
        if (name === "permanent_state1") {
          updated.permanent_state1 = value;
        }
        if (name === "permanent_country1") {
          updated.permanent_country1 = value;
        }
      }

      return updated;
    });
  };

  // Education field mapper for EducationFields component
  // Maps EducationFields field names to FamilyMemberDialog field names
  const handleEducationFieldChange = (e) => {
    const { name, value } = e.target;
    
    // EducationFields uses: qualification, board, course, educationtype, instituteschool,
    // institutecountry, institutestate, instituecity, institutepincode, startyear_c, currentlystudying_c, endyear_c, grade
    // FamilyMemberDialog uses: education1_c, educationtype1_c, institute1_c, yearofpassing1_c (and 2,3,4,5)
    
    // Extract section number from field name (e.g., "qualification1" -> 1)
    const match = name.match(/(\d+)$/);
    if (!match) {
      // No section number, pass through as is
      setNewMember((prev) => ({ ...prev, [name]: value }));
      return;
    }
    
    const section = match[1];
    const fieldBase = name.replace(/\d+$/, '');
    
    // Map field names
    const fieldMap = {
      'qualification': `education${section}_c`,
      'board': `board${section}_c`,
      'course': `course${section}_c`,
      'educationtype': `educationtype${section}_c`,
      'instituteschool': `institute${section}_c`,
      'institutecountry': `institutecountry${section}_c`,
      'institutestate': `institutestate${section}_c`,
      'instituecity': `institutecity${section}_c`,
      'institutepincode': `institutepincode${section}_c`,
      'startyear_c': `startyear${section}_c`,
      'currentlystudying_c': `currentlystudying${section}_c`,
      'endyear_c': `endyear${section}_c`,
      'grade': `grade${section}_c`,
      'educationimage': `educationimage${section}`,
    };
    
    const mappedName = fieldMap[fieldBase] || name;
    
    setNewMember((prev) => {
      const updated = { ...prev, [mappedName]: value };
      return updated;
    });
  };

  // Add new education section
  const handleAddEducationSection = () => {
    if (visibleEducationSections >= maxEducationSections) {
      return;
    }

    const currentSection = visibleEducationSections;
    
    if (!isEducationSectionPrimaryFieldsFilled(currentSection)) {
      const requiredFields = getEducationRequiredFields(currentSection);
      
      const missingFields = requiredFields.filter((fieldName) => {
        // Map to storage field name before checking value
        const storageFieldName = mapEducationFieldNameToStorage(fieldName);
        const value = newMember[storageFieldName];
        if (typeof value === "string") {
          return value.trim() === "";
        }
        return value === undefined || value === null || value === "";
      });

      if (missingFields.length > 0) {
        setEducationFieldErrors((prev) => {
          const next = { ...prev };
          missingFields.forEach((fieldName) => {
            next[fieldName] = "Please fill this field before adding another education entry.";
          });
          return next;
        });
      }

      setEducationSectionError("Please fill the current education details first.");
      return;
    }

    // Clear errors for current section
    const requiredFields = getEducationRequiredFields(currentSection);
    setEducationFieldErrors((prev) => {
      const next = { ...prev };
      requiredFields.forEach((fieldName) => {
        delete next[fieldName];
      });
      return next;
    });

    const nextSection = visibleEducationSections + 1;
    
    // Set default value for education type in the new section
    setNewMember((prev) => ({
      ...prev,
      [`educationtype${nextSection}_c`]: "Regular",
    }));

    setVisibleEducationSections((prev) => Math.min(prev + 1, maxEducationSections));
    setEducationSectionError("");
  };

  // Remove education section
  const handleRemoveEducationSection = (section) => {
    setEducationSectionError("");

    // Clear all fields for sections >= the removed section
    setNewMember((prev) => {
      const updated = { ...prev };
      for (let i = section; i < maxEducationSections; i++) {
        const currentSection = i;
        const nextSection = i + 1;

        // Copy data from next section to current section
        updated[`qualification${currentSection}`] = updated[`qualification${nextSection}`] || "";
        updated[`board${currentSection}`] = updated[`board${nextSection}`] || "";
        updated[`course${currentSection}`] = updated[`course${currentSection}`] || "";
        updated[`educationtype${currentSection}`] = updated[`educationtype${nextSection}`] || "";
        updated[`instituteschool${currentSection}`] = updated[`instituteschool${nextSection}`] || "";
        updated[`institutecountry${currentSection}`] = updated[`institutecountry${nextSection}`] || "";
        updated[`institutestate${currentSection}`] = updated[`institutestate${nextSection}`] || "";
        updated[`instituecity${currentSection}`] = updated[`instituecity${nextSection}`] || "";
        updated[`institutepincode${currentSection}`] = updated[`institutepincode${nextSection}`] || "";
        updated[`startyear${currentSection}_c`] = updated[`startyear${nextSection}_c`] || "";
        updated[`currentlystudying${currentSection}_c`] = updated[`currentlystudying${nextSection}_c`] || false;
        updated[`endyear${currentSection}_c`] = updated[`endyear${nextSection}_c`] || "";
        updated[`grade${currentSection}_c`] = updated[`grade${nextSection}_c`] || "";
        updated[`educationimage${currentSection}`] = updated[`educationimage${nextSection}`] || "";
      }

      // Clear the last section
      const lastSection = maxEducationSections;
      updated[`qualification${lastSection}`] = "";
      updated[`board${lastSection}`] = "";
      updated[`course${lastSection}`] = "";
      updated[`educationtype${lastSection}`] = "";
      updated[`instituteschool${lastSection}`] = "";
      updated[`institutecountry${lastSection}`] = "";
      updated[`institutestate${lastSection}`] = "";
      updated[`instituecity${lastSection}`] = "";
      updated[`institutepincode${lastSection}`] = "";
      updated[`startyear${lastSection}_c`] = "";
      updated[`currentlystudying${lastSection}_c`] = false;
      updated[`endyear${lastSection}_c`] = "";
      updated[`grade${lastSection}_c`] = "";
  updated[`educationimage${lastSection}`] = "";

      return updated;
    });

    setVisibleEducationSections((prev) => Math.max(prev - 1, 1));

    // Clear field errors for removed section
    const requiredFields = getEducationRequiredFields(section);
    setEducationFieldErrors((prev) => {
      const next = { ...prev };
      requiredFields.forEach((fieldName) => {
        delete next[fieldName];
      });
      return next;
    });
  };

  // Work Section Handlers
  const getWorkSectionRequiredFields = useCallback(
    (section) => [
      `companyname${section}`,
      `designation${section}`,
      `employmenttype${section}`,
      `startdate${section}`,
    ],
    []
  );

  const isWorkSectionPrimaryFieldsFilled = (section) => {
    const requiredFields = getWorkSectionRequiredFields(section);

    return requiredFields.every((fieldName) => {
      const value = newMember[fieldName];
      if (typeof value === "string") {
        return value.trim() !== "";
      }
      return value !== undefined && value !== null && value !== "";
    });
  };

  // Auto-clear work section errors when fields are filled
  useEffect(() => {
    if (workSectionError) {
      const currentSection = visibleWorkSections;
      if (isWorkSectionPrimaryFieldsFilled(currentSection)) {
        setWorkSectionError("");
        const fieldsToClear = getWorkSectionRequiredFields(currentSection);
        setWorkFieldErrors((prev) => {
          const next = { ...prev };
          fieldsToClear.forEach((fieldName) => {
            delete next[fieldName];
          });
          return next;
        });
      }
    }
  }, [workSectionError, visibleWorkSections, newMember, getWorkSectionRequiredFields]);

  const handleAddWorkSection = () => {
    if (visibleWorkSections >= maxWorkSections) {
      return;
    }

    const currentSection = visibleWorkSections;
    if (!isWorkSectionPrimaryFieldsFilled(currentSection)) {
      const requiredFields = getWorkSectionRequiredFields(currentSection);
      const missingFields = requiredFields.filter((fieldName) => {
        const value = newMember[fieldName];
        if (typeof value === "string") {
          return value.trim() === "";
        }
        return value === undefined || value === null || value === "";
      });

      if (missingFields.length > 0) {
        setWorkFieldErrors((prev) => {
          const next = { ...prev };
          missingFields.forEach((fieldName) => {
            next[fieldName] = "Please fill this field before adding another work experience entry.";
          });
          return next;
        });
      }

      setWorkSectionError("Please fill the current work experience details first.");
      return;
    }

    const requiredFields = getWorkSectionRequiredFields(currentSection);
    setWorkFieldErrors((prev) => {
      const next = { ...prev };
      requiredFields.forEach((fieldName) => {
        delete next[fieldName];
      });
      return next;
    });

    setVisibleWorkSections((prev) => Math.min(prev + 1, maxWorkSections));
    setWorkSectionError("");
  };

  const handleRemoveWorkSection = (section) => {
    setWorkSectionError("");

    setNewMember((prev) => {
      const updated = { ...prev };

      for (let current = section; current <= maxWorkSections; current++) {
        const nextSection = current + 1;

        const fieldMappings = [
          'companyname', 'designation', 'department', 'employmenttype', 'sector',
          'description', 'startdate', 'enddate', 'currentlyworking',
          'address', 'country', 'state', 'city', 'pincode'
        ];

        fieldMappings.forEach((fieldPrefix) => {
          const currentKey = `${fieldPrefix}${current}`;
          const nextKey = `${fieldPrefix}${nextSection}`;

          if (nextSection <= maxWorkSections) {
            updated[currentKey] = prev[nextKey] ?? "";
          } else {
            updated[currentKey] = "";
          }
        });
      }

      return updated;
    });

    setVisibleWorkSections((prev) => Math.max(1, prev - 1));
    setWorkFieldErrors({});
  };

  // ID Card Section Handlers
  const idCardFieldKeys = [
    "idCardEntryId",
    "idtype",
    "idcardname",
    "idnumber",
    "issuedate",
    "expirydate",
    "idcarddescription",
  ];

  const getIDCardSectionRequiredFields = useCallback(
    (section) => [
      `idtype${section}`,
      `idcardname${section}`,
      `idnumber${section}`,
    ],
    []
  );

  const isIDCardSectionPrimaryFieldsFilled = (section) => {
    const requiredFields = getIDCardSectionRequiredFields(section);

    return requiredFields.every((fieldName) => {
      const value = newMember[fieldName];
      if (typeof value === "string") {
        return value.trim() !== "";
      }
      return value !== undefined && value !== null && value !== "";
    });
  };

  const computeVisibleIDCardSections = useCallback(
    (data) => {
      let maxFilledSection = 0;
      for (let i = 1; i <= maxIDCardSections; i++) {
        const hasData = [
          data[`idtype${i}`],
          data[`idcardname${i}`],
          data[`idnumber${i}`],
        ].some((value) => {
          if (typeof value === "string") {
            return value.trim() !== "";
          }
          return value !== undefined && value !== null && value !== "";
        });

        if (hasData) {
          maxFilledSection = i;
        }
      }

      return Math.max(1, maxFilledSection);
    },
    [maxIDCardSections]
  );

  // Auto-clear ID card section errors when fields are filled
  useEffect(() => {
    if (idCardSectionError) {
      const currentSection = visibleIDCardSections;
      if (isIDCardSectionPrimaryFieldsFilled(currentSection)) {
        setIDCardSectionError("");
        const fieldsToClear = getIDCardSectionRequiredFields(currentSection);
        setIDCardFieldErrors((prev) => {
          const next = { ...prev };
          fieldsToClear.forEach((fieldName) => {
            delete next[fieldName];
          });
          return next;
        });
      }
    }
  }, [idCardSectionError, visibleIDCardSections, newMember, getIDCardSectionRequiredFields]);

  const handleAddIDCardSection = () => {
    if (visibleIDCardSections >= maxIDCardSections) {
      return;
    }

    const currentSection = visibleIDCardSections;
    if (!isIDCardSectionPrimaryFieldsFilled(currentSection)) {
      const requiredFields = getIDCardSectionRequiredFields(currentSection);
      const missingFields = requiredFields.filter((fieldName) => {
        const value = newMember[fieldName];
        if (typeof value === "string") {
          return value.trim() === "";
        }
        return value === undefined || value === null || value === "";
      });

      if (missingFields.length > 0) {
        setIDCardFieldErrors((prev) => {
          const next = { ...prev };
          missingFields.forEach((fieldName) => {
            next[fieldName] = "Please fill this field before adding another ID card entry.";
          });
          return next;
        });
      }

      setIDCardSectionError("Please fill the current ID card details first.");
      return;
    }

    const requiredFields = getIDCardSectionRequiredFields(currentSection);
    setIDCardFieldErrors((prev) => {
      const next = { ...prev };
      requiredFields.forEach((fieldName) => {
        delete next[fieldName];
      });
      return next;
    });

    setVisibleIDCardSections((prev) => Math.min(prev + 1, maxIDCardSections));
    setIDCardSectionError("");
  };

  const handleRemoveIDCardSection = (section) => {
    setIDCardSectionError("");

    setNewMember((prev) => {
      const updated = { ...prev };

      for (let current = section; current <= maxIDCardSections; current++) {
        const nextSection = current + 1;

        idCardFieldKeys.forEach((fieldKey) => {
          const nextKey = `${fieldKey}${nextSection}`;
          const currentKey = `${fieldKey}${current}`;

          if (nextSection <= maxIDCardSections) {
            updated[currentKey] = prev[nextKey] ?? "";
          } else {
            updated[currentKey] = "";
          }
        });
      }

      setVisibleIDCardSections(computeVisibleIDCardSections(updated));
      return updated;
    });

    setIDCardFieldErrors({});
  };

  // Bank Details Section Handlers
  const bankFieldKeys = [
    "bankEntryId",
    "accountholdername",
    "accountnumber",
    "ifsc",
    "accounttype",
    "bankname",
    "branch",
    "bankcountry",
    "status",
    "bankdescription",
    "image",
  ];

  const getBankSectionRequiredFields = useCallback(
    (section) => [
      `accountholdername${section}`,
      `accountnumber${section}`,
      `ifsc${section}`,
      `bankname${section}`,
    ],
    []
  );

  const isBankSectionPrimaryFieldsFilled = (section) => {
    const requiredFields = getBankSectionRequiredFields(section);

    return requiredFields.every((fieldName) => {
      const value = newMember[fieldName];
      if (typeof value === "string") {
        return value.trim() !== "";
      }
      return value !== undefined && value !== null && value !== "";
    });
  };

  const computeVisibleBankSections = useCallback(
    (data) => {
      let maxFilledSection = 0;
      for (let i = 1; i <= maxBankSections; i++) {
        const hasData = [
          data[`accountholdername${i}`],
          data[`accountnumber${i}`],
          data[`ifsc${i}`],
          data[`bankname${i}`],
        ].some((value) => {
          if (typeof value === "string") {
            return value.trim() !== "";
          }
          return value !== undefined && value !== null && value !== "";
        });

        if (hasData) {
          maxFilledSection = i;
        }
      }

      return Math.max(1, maxFilledSection);
    },
    [maxBankSections]
  );

  // Auto-clear bank section errors when fields are filled
  useEffect(() => {
    if (bankSectionError) {
      const currentSection = visibleBankSections;
      if (isBankSectionPrimaryFieldsFilled(currentSection)) {
        setBankSectionError("");
        const fieldsToClear = getBankSectionRequiredFields(currentSection);
        setBankFieldErrors((prev) => {
          const next = { ...prev };
          fieldsToClear.forEach((fieldName) => {
            delete next[fieldName];
          });
          return next;
        });
      }
    }
  }, [bankSectionError, visibleBankSections, newMember, getBankSectionRequiredFields]);

  const handleAddBankSection = () => {
    if (visibleBankSections >= maxBankSections) {
      return;
    }

    const currentSection = visibleBankSections;
    if (!isBankSectionPrimaryFieldsFilled(currentSection)) {
      const requiredFields = getBankSectionRequiredFields(currentSection);
      const missingFields = requiredFields.filter((fieldName) => {
        const value = newMember[fieldName];
        if (typeof value === "string") {
          return value.trim() === "";
        }
        return value === undefined || value === null || value === "";
      });

      if (missingFields.length > 0) {
        setBankFieldErrors((prev) => {
          const next = { ...prev };
          missingFields.forEach((fieldName) => {
            next[fieldName] = "Please fill this field before adding another bank entry.";
          });
          return next;
        });
      }

      setBankSectionError("Please fill the current bank details first.");
      return;
    }

    const requiredFields = getBankSectionRequiredFields(currentSection);
    setBankFieldErrors((prev) => {
      const next = { ...prev };
      requiredFields.forEach((fieldName) => {
        delete next[fieldName];
      });
      return next;
    });

    setVisibleBankSections((prev) => Math.min(prev + 1, maxBankSections));
    setBankSectionError("");
  };

  const handleRemoveBankSection = (section) => {
    setBankSectionError("");

    setNewMember((prev) => {
      const updated = { ...prev };

      for (let current = section; current <= maxBankSections; current++) {
        const nextSection = current + 1;

        bankFieldKeys.forEach((fieldKey) => {
          const nextKey = `${fieldKey}${nextSection}`;
          const currentKey = `${fieldKey}${current}`;

          if (nextSection <= maxBankSections) {
            updated[currentKey] = prev[nextKey] ?? "";
          } else {
            updated[currentKey] = "";
          }
        });
      }

      setVisibleBankSections(computeVisibleBankSections(updated));
      return updated;
    });

    setBankFieldErrors({});
  };

  // Social Links Section Handlers
  const socialLinksFieldKeys = [
    "socialLinksEntryId",
    "socialtype",
    "sociallink",
  ];

  const getSocialLinksSectionRequiredFields = useCallback(
    (section) => [
      `socialtype${section}`,
      `sociallink${section}`,
    ],
    []
  );

  const isSocialLinksSectionPrimaryFieldsFilled = (section) => {
    const requiredFields = getSocialLinksSectionRequiredFields(section);

    return requiredFields.every((fieldName) => {
      const value = newMember[fieldName];
      if (typeof value === "string") {
        return value.trim() !== "";
      }
      return value !== undefined && value !== null && value !== "";
    });
  };

  const computeVisibleSocialLinksSections = useCallback(
    (data) => {
      let maxFilledSection = 0;
      for (let i = 1; i <= maxSocialLinksSections; i++) {
        const hasData = [
          data[`socialtype${i}`],
          data[`sociallink${i}`],
        ].some((value) => {
          if (typeof value === "string") {
            return value.trim() !== "";
          }
          return value !== undefined && value !== null && value !== "";
        });

        if (hasData) {
          maxFilledSection = i;
        }
      }

      return Math.max(1, maxFilledSection);
    },
    [maxSocialLinksSections]
  );

  // Auto-clear social links section errors when fields are filled
  useEffect(() => {
    if (socialLinksSectionError) {
      const currentSection = visibleSocialLinksSections;
      if (isSocialLinksSectionPrimaryFieldsFilled(currentSection)) {
        setSocialLinksSectionError("");
        const fieldsToClear = getSocialLinksSectionRequiredFields(currentSection);
        setSocialLinksFieldErrors((prev) => {
          const next = { ...prev };
          fieldsToClear.forEach((fieldName) => {
            delete next[fieldName];
          });
          return next;
        });
      }
    }
  }, [socialLinksSectionError, visibleSocialLinksSections, newMember, getSocialLinksSectionRequiredFields]);

  const handleAddSocialLinksSection = () => {
    if (visibleSocialLinksSections >= maxSocialLinksSections) {
      return;
    }

    const currentSection = visibleSocialLinksSections;
    if (!isSocialLinksSectionPrimaryFieldsFilled(currentSection)) {
      const requiredFields = getSocialLinksSectionRequiredFields(currentSection);
      const missingFields = requiredFields.filter((fieldName) => {
        const value = newMember[fieldName];
        if (typeof value === "string") {
          return value.trim() === "";
        }
        return value === undefined || value === null || value === "";
      });

      if (missingFields.length > 0) {
        setSocialLinksFieldErrors((prev) => {
          const next = { ...prev };
          missingFields.forEach((fieldName) => {
            next[fieldName] = "Please fill this field before adding another social link.";
          });
          return next;
        });
      }

      setSocialLinksSectionError("Please fill the current social link details first.");
      return;
    }

    const requiredFields = getSocialLinksSectionRequiredFields(currentSection);
    setSocialLinksFieldErrors((prev) => {
      const next = { ...prev };
      requiredFields.forEach((fieldName) => {
        delete next[fieldName];
      });
      return next;
    });

    setVisibleSocialLinksSections((prev) => Math.min(prev + 1, maxSocialLinksSections));
    setSocialLinksSectionError("");
  };

  const handleRemoveSocialLinksSection = (section) => {
    setSocialLinksSectionError("");

    setNewMember((prev) => {
      const updated = { ...prev };

      for (let current = section; current <= maxSocialLinksSections; current++) {
        const nextSection = current + 1;

        socialLinksFieldKeys.forEach((fieldKey) => {
          const nextKey = `${fieldKey}${nextSection}`;
          const currentKey = `${fieldKey}${current}`;

          if (nextSection <= maxSocialLinksSections) {
            updated[currentKey] = prev[nextKey] ?? "";
          } else {
            updated[currentKey] = "";
          }
        });
      }

      setVisibleSocialLinksSections(computeVisibleSocialLinksSections(updated));
      return updated;
    });

    setSocialLinksFieldErrors({});
  };

  const handlePincodeLocationChange = (locationData) => {
    if (locationData) {
      setNewMember((prev) => ({
        ...prev,
        city: locationData.city || prev.city,
        // populate village/town and taluk if payload contains them
        villageTown_c: locationData.villageTown || prev.villageTown_c,
        taluk_c: locationData.taluk || prev.taluk_c,
        state: locationData.state
          ? allStates.find((s) => s.name.toLowerCase() === locationData.state.toLowerCase()) ||
            prev.state
          : prev.state,
        country: locationData.country
          ? allCountries.find((c) => c.name.toLowerCase() === locationData.country.toLowerCase()) ||
            prev.country
          : prev.country,
      }));
    }
  };

  const handlePincodeChange = (pincode) => {
    setNewMember((prev) => ({
      ...prev,
      pincode: pincode || "",
    }));
  };

  const handleIdChange = (event, newValue) => {
    setNewMember((prev) => ({ ...prev, relation: newValue || "" }));
  };

  const handleRelationInputChange = (event, newInputValue) => {
    setNewMember((prev) => ({ ...prev, relation: newInputValue || "" }));
  };

  const handleCasteChange = (event, newValue) => {
    setNewMember((prev) => ({ ...prev, caste: newValue || "" }));
  };

  const handleCasteInputChange = (event, newInputValue) => {
    setNewMember((prev) => ({ ...prev, caste: newInputValue || "" }));
  };

  const handleReligionChange = (event, newValue) => {
    setNewMember((prev) => ({ ...prev, religion: newValue || "" }));
  };

  const handleReligionInputChange = (event, newInputValue) => {
    setNewMember((prev) => ({ ...prev, religion: newInputValue || "" }));
  };

  const handleCityChange = (event, newValue) => {
    setNewMember((prev) => ({ ...prev, city: newValue || "" }));
  };

  const handleCityInputChange = (event, newInputValue) => {
    setNewMember((prev) => ({ ...prev, city: newInputValue || "" }));
  };

  // Helpers for "Add to master data" UX
  const sortByName = (arr = []) =>
    [...arr].sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));

  const shouldOfferAddRelation = useMemo(() => {
    const val = (newMember.relation || "").trim();
    if (!val) return false;
    const names = (relationships || []).map((i) => (i?.name || "").toLowerCase());
    return !names.includes(val.toLowerCase());
  }, [newMember.relation, relationships]);

  const shouldOfferAddCaste = useMemo(() => {
    const val = (newMember.caste || "").trim();
    if (!val) return false;
    const names = (castes || []).map((i) => (i?.name || "").toLowerCase());
    return !names.includes(val.toLowerCase());
  }, [newMember.caste, castes]);

  const shouldOfferAddReligion = useMemo(() => {
    const val = (newMember.religion || "").trim();
    if (!val) return false;
    const names = (religions || []).map((i) => (i?.name || "").toLowerCase());
    return !names.includes(val.toLowerCase());
  }, [newMember.religion, religions]);

  // Utility to check if a value exists in a master list (case-insensitive)
  const listIncludes = (coll, val) => {
    const v = (val || "").trim().toLowerCase();
    if (!v) return false;
    return (coll || []).some((i) => (((i?.name ?? i) || "").toLowerCase()) === v);
  };

  // Validation flags: if user typed a non-empty value that is not in the list, mark invalid
  const relationInvalid = useMemo(() => {
    if (mode === "edit") return false; // legacy records may have free-form values
    const val = (newMember.relation || "").trim();
    if (!val) return false; // required is already enforced separately
    if (val.toLowerCase() === "myself") return false; // "Myself" is always valid
    return !listIncludes(relationships, val);
  }, [mode, newMember.relation, relationships]);

  const casteInvalid = useMemo(() => {
    const val = (newMember.caste || "").trim();
    if (!val || val.toLowerCase() === "not specified") return false; // allow empty or "Not specified"
    return !listIncludes(castes, val);
  }, [newMember.caste, castes]);

  const religionInvalid = useMemo(() => {
    const val = (newMember.religion || "").trim();
    if (!val || val.toLowerCase() === "not specified") return false; // allow empty or "Not specified"
    return !listIncludes(religions, val);
  }, [newMember.religion, religions]);

  const openAddMasterDialog = (category, value) => {
    setAddDialog({ open: true, category, initialValue: value || "" });
  };
  const closeAddMasterDialog = () => {
    setAddDialog((prev) => ({ ...prev, open: false }));
  };
  const handleAddMasterSuccess = (created) => {
    if (!created?.name || !created?.category_c) {
      closeAddMasterDialog();
      return;
    }
    switch (created.category_c) {
      case "Caste":
        setCastes((prev) => sortByName([...(prev || []), created]));
        setNewMember((p) => ({ ...p, caste: created.name }));
        break;
      case "Religion":
        setReligions((prev) => sortByName([...(prev || []), created]));
        setNewMember((p) => ({ ...p, religion: created.name }));
        break;
      case "Relationship":
        setRelationships((prev) => sortByName([...(prev || []), created]));
        setNewMember((p) => ({ ...p, relation: created.name }));
        break;
      default:
        break;
    }
    setSnackbarMessage(`${created.category_c} added`);
    setSuccessSB(true);
    closeAddMasterDialog();
  };

  // Handle field blur to mark it as touched
  const handleFieldBlur = (fieldName) => {
    setTouchedFields(prev => ({
      ...prev,
      [fieldName]: true
    }));
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file (JPG, PNG, GIF)');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
  setError("Image must be smaller than 15MB");
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setNewMember((prev) => ({
        ...prev,
        photo: reader.result,
        photoFile: file,
      }));
      setError(null);
    };
    reader.onerror = () => {
      setError('Error reading the image file');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };
    reader.readAsDataURL(file);
  };

  const handleOpenCamera = useCallback(async () => {
    stopCameraStream();
    setCapturedImage(null);
    setCameraReady(false);
    setCameraError(null);
    setCameraMessage('Requesting camera access...');
    setCameraRetryCount(0);
    resetTriedDevices();
    setWebcamKey(-1);
    setLoosenConstraints(false);
    setForceDefaultDevice(false);
    setUseBareConstraints(false);
    setCameraDialogOpen(true);

    // Allow dialog to render and previous streams to stop
    await new Promise((resolve) => setTimeout(resolve, 500));

    try {
      const devices = await fetchVideoDevices();
      setVideoDevices(devices);
      const hasSingleDevice = devices.length === 1;
      if (hasSingleDevice) {
        setForceDefaultDevice(true);
      }
      const deviceId =
        selectDeviceIdForMode(devices, facingMode) || devices[0]?.deviceId || null;

      if (!deviceId) {
        throw new Error('No camera devices found.');
      }

      setActiveDevice(deviceId);
      setCameraMessage('Initializing camera...');

      await new Promise((resolve) => setTimeout(resolve, 500));
      setWebcamKey((prev) => prev + 1);
    } catch (initError) {
      console.error('Camera initialization error:', initError);
      setCameraMessage(null);
      setCameraError(initError.message || 'Unable to access camera. Please check permissions.');
    }
  }, [stopCameraStream, resetTriedDevices, fetchVideoDevices, selectDeviceIdForMode, facingMode, setActiveDevice]);

  const handleCloseCamera = useCallback(() => {
    setCameraDialogOpen(false);
    setCapturedImage(null);
    setCameraReady(false);
    setCameraError(null);
    setCameraMessage(null);
    setSelectedDeviceId(null);
    setCameraRetryCount(0);
    resetTriedDevices();
    setWebcamKey(-1);
    setLoosenConstraints(false);
    setForceDefaultDevice(false);
    setUseBareConstraints(false);
    stopCameraStream();
  }, [resetTriedDevices, stopCameraStream]);

  const capturePhoto = () => {
    const imageSrc = webcamRef.current?.getScreenshot();
    setCapturedImage(imageSrc);
  };

  const handleConfirmPhoto = useCallback(() => {
    if (capturedImage) {
      fetch(capturedImage)
        .then((res) => res.blob())
        .then((blob) => {
          const file = new File([blob], 'captured-photo.jpg', { type: 'image/jpeg' });
          setNewMember((prev) => ({
            ...prev,
            photo: capturedImage,
            photoFile: file,
          }));
          setError(null);
        })
        .finally(() => {
          setCapturedImage(null);
        });
    }
    setCameraDialogOpen(false);
    setCameraMessage(null);
    stopCameraStream();
  }, [capturedImage, setNewMember, stopCameraStream]);

  const handleRetakePhoto = () => {
    setCapturedImage(null);
    setCameraMessage(null);
  };

  const switchCamera = useCallback(async () => {
    stopCameraStream();
    setCameraReady(false);
    setCameraError(null);
    setCameraRetryCount(0);
    setWebcamKey(-1);
    setLoosenConstraints(false);
    setForceDefaultDevice(false);
    setUseBareConstraints(false);

    await new Promise((resolve) => setTimeout(resolve, 600));

    try {
      const devices = videoDevices.length ? videoDevices : await fetchVideoDevices();
      if (!videoDevices.length) {
        setVideoDevices(devices);
      }

      if (devices.length <= 1) {
        setFacingMode('user');
        setForceDefaultDevice(true);
        setCameraMessage('Only one camera detected on this device.');
        setWebcamKey((prev) => prev + 1);
        return;
      }

      const nextMode = facingMode === 'user' ? 'environment' : 'user';
      setFacingMode(nextMode);
      setCameraMessage(`Switching to ${nextMode === 'user' ? 'front' : 'rear'} camera...`);

      resetTriedDevices();
      const nextDeviceId =
        selectDeviceIdForMode(devices, nextMode) || devices[0]?.deviceId || null;

      if (!nextDeviceId) {
        throw new Error('No camera devices found.');
      }

      setActiveDevice(nextDeviceId);

      await new Promise((resolve) => setTimeout(resolve, 500));
      setWebcamKey((prev) => prev + 1);
    } catch (switchError) {
      console.error('Switch camera failed:', switchError);
      setCameraMessage(null);
      setCameraError(switchError.message || 'Unable to switch camera. Please try again.');
    }
  }, [stopCameraStream, videoDevices, fetchVideoDevices, facingMode, resetTriedDevices, selectDeviceIdForMode, setActiveDevice]);

  const retryCamera = useCallback(async () => {
    stopCameraStream();
    setCameraError(null);
    setCameraReady(false);
    setCameraMessage('Retrying camera...');
    setCameraRetryCount(0);
    resetTriedDevices();
    setWebcamKey(-1);
    setLoosenConstraints(false);
    setForceDefaultDevice(false);
    setUseBareConstraints(false);

    await new Promise((resolve) => setTimeout(resolve, 700));

    try {
      const devices = videoDevices.length ? videoDevices : await fetchVideoDevices();
      if (!videoDevices.length) {
        setVideoDevices(devices);
      }

      const hasSingleDevice = devices.length === 1;
      setForceDefaultDevice(hasSingleDevice);

      const deviceId =
        selectDeviceIdForMode(devices, facingMode) || devices[0]?.deviceId || null;

      if (!deviceId) {
        throw new Error('No camera devices found.');
      }

      setActiveDevice(deviceId);

      await new Promise((resolve) => setTimeout(resolve, 500));
      setWebcamKey((prev) => prev + 1);
    } catch (retryError) {
      console.error('Retry camera failed:', retryError);
      setCameraMessage(null);
      setCameraError(retryError.message || 'Unable to access camera. Please check permissions.');
    }
  }, [stopCameraStream, videoDevices, fetchVideoDevices, resetTriedDevices, selectDeviceIdForMode, facingMode, setActiveDevice]);

  const handleCameraReady = useCallback(() => {
    setCameraReady(true);
    setCameraError(null);
    setCameraMessage(null);
    setCameraRetryCount(0);
    setLoosenConstraints(false);
    setForceDefaultDevice(false);
    setUseBareConstraints(false);
  }, []);

  const handleCameraError = useCallback(async (error) => {
    console.error('Camera error:', error);
    console.error('Camera error details', {
      name: error?.name,
      message: error?.message,
      code: error?.code,
      facingMode,
      selectedDeviceId,
      loosenConstraints,
      triedDevices: Array.from(triedDevicesRef.current),
      retryCount: cameraRetryCount,
      timestamp: new Date().toISOString(),
    });
    stopCameraStream();
    setCameraReady(false);
    setCameraError(null);
    setWebcamKey(-1);

    if (error?.name === 'NotAllowedError' || error?.name === 'PermissionDeniedError') {
      setCameraMessage(null);
      setCameraError('Camera permission was denied. Please allow camera access in your browser settings and try again.');
      return;
    }

    if (error?.name === 'NotFoundError') {
      setCameraMessage(null);
      setCameraError('No camera devices were found. Please connect a camera and try again.');
      return;
    }

    if (error?.name === 'NotReadableError') {
      setCameraMessage(null);
      setCameraError('Your camera appears to be in use by another application. Please close any app using the camera and try again.');
      return;
    }

    if (error?.name === 'OverconstrainedError') {
      setCameraMessage(null);
      setCameraError('The selected camera cannot satisfy the requested settings. Try switching cameras or close other apps that may be reserving it.');
      return;
    }

    let devices = videoDevices;

    if (!devices.length) {
      try {
        devices = await fetchVideoDevices();
        setVideoDevices(devices);
      } catch (deviceError) {
        setCameraMessage(null);
        setCameraError(deviceError.message || 'Unable to access camera. Please check permissions.');
        return;
      }
    }

    if (error?.name === 'AbortError') {
      await new Promise((resolve) => setTimeout(resolve, 600));

      const scheduleReinitialization = (delay = 900) => {
        setTimeout(() => {
          stopCameraStream();
          setCameraError(null);
          setCameraReady(false);
          setWebcamKey((prev) => prev + 1);
        }, delay);
      };

      const alternateDeviceId = selectDeviceIdForMode(devices, facingMode, { preferUntried: true });

      if (alternateDeviceId && !triedDevicesRef.current.has(alternateDeviceId)) {
        setCameraMessage('Trying another camera device...');
        setActiveDevice(alternateDeviceId);
        await new Promise((resolve) => setTimeout(resolve, 500));
        setWebcamKey((prev) => prev + 1);
        return;
      }

      if (facingMode === 'environment') {
        const frontDeviceId =
          selectDeviceIdForMode(devices, 'user', { preferUntried: true }) ||
          selectDeviceIdForMode(devices, 'user');

        if (frontDeviceId && !triedDevicesRef.current.has(frontDeviceId)) {
          setCameraMessage('Rear camera not available. Switching to front camera...');
          resetTriedDevices();
          setActiveDevice(frontDeviceId);
          setFacingMode('user');
          await new Promise((resolve) => setTimeout(resolve, 500));
          setWebcamKey((prev) => prev + 1);
          return;
        }
      }

      if (!loosenConstraints) {
        setCameraMessage('Camera timed out. Retrying with lower video quality...');
        setLoosenConstraints(true);
        setCameraRetryCount((prev) => Math.min(prev + 1, MAX_CAMERA_RETRIES));
        scheduleReinitialization();
        return;
      }

      if (!forceDefaultDevice) {
        setCameraMessage('Camera timed out. Switching to browser default camera...');
        setForceDefaultDevice(true);
        resetTriedDevices();
        setSelectedDeviceId(null);
        setFacingMode('user');
        setCameraRetryCount((prev) => Math.min(prev + 1, MAX_CAMERA_RETRIES));
        scheduleReinitialization();
        return;
      }

      if (!useBareConstraints) {
        setCameraMessage('Camera timed out. Retrying with minimal settings...');
        setUseBareConstraints(true);
        setCameraRetryCount((prev) => Math.min(prev + 1, MAX_CAMERA_RETRIES));
        scheduleReinitialization(1000);
        return;
      }

      if (cameraRetryCount + 1 <= MAX_CAMERA_RETRIES) {
        setCameraMessage('Camera still loading. Retrying...');
        setCameraRetryCount((prev) => Math.min(prev + 1, MAX_CAMERA_RETRIES));
        scheduleReinitialization();
        return;
      }

      setCameraMessage(null);
      setCameraError('Camera initialization timed out. Please try again or check your camera permissions.');
      return;
    }

    setCameraMessage(null);
    setCameraError('Unable to access camera. Please check permissions.');
  }, [videoDevices, fetchVideoDevices, selectDeviceIdForMode, facingMode, cameraRetryCount, stopCameraStream, setActiveDevice, resetTriedDevices, setVideoDevices, loosenConstraints, selectedDeviceId, forceDefaultDevice, useBareConstraints]);

  const videoConstraints = useMemo(() => {
    if (useBareConstraints) {
      return null;
    }

    const baseConstraints = loosenConstraints
      ? {
          width: { ideal: 640, min: 320 },
          height: { ideal: 480, min: 240 },
          frameRate: { ideal: 24, min: 10 },
        }
      : {
          width: { ideal: 640 },
          height: { ideal: 480 },
          frameRate: { ideal: 30 },
        };

    if (forceDefaultDevice) {
      return baseConstraints;
    }

    if (selectedDeviceId) {
      return {
        ...baseConstraints,
        deviceId: { ideal: selectedDeviceId },
      };
    }

    return {
      ...baseConstraints,
      facingMode: { ideal: facingMode },
    };
  }, [facingMode, selectedDeviceId, forceDefaultDevice, loosenConstraints, useBareConstraints]);

  const handleClear = () => {
    setNewMember({
      name: "",
      mobile: "",
      phoneCountry: "IN",
      email: "",
      relation: "",
      aadhar: "",
      wifeId: "",
      photo: null,
      photoFile: null,
      caste: "",
      religion: "",
      address: "",
      city: "",
      pincode: "",
      state: null,
      country: null,
      instagram: "",
      youtube: "",
      facebook: "",
      threads: "",
      linkedin: "",
      instagram_c: "",
      youtube_c: "",
      facebook_c: "",
      threads_c: "",
      linkedin_c: "",
      // Personal Information
      address1_c: "",
      address2_c: "",
      age_c: "",
      alivestatus_c: "Alive",
      birthplace_c: "",
      bloodgroup_c: "",
      city_c: "",
      country_c: "",
      district_c: "",
      dateofdeath_c: "",
      deathplace_c: "",
      deathreason_c: "",
      deathaddress_c: "",
      deatharea_c: "",
      deathpincode_c: "",
      deathcity_c: "",
      deathstate_c: null,
      deathcountry_c: null,
      disability_c: "",
      dob_c: "",
      gender_c: "",
      languagesknown_c: "",
      maritalstatus_c: "",
      marriagedate_c: "",
      marriageplace_c: "",
      mothertongue_c: "",
      pincode_c: "",
      state_c: "",
  villageTown_c: "",
  taluk_c: "",
  district_c: "",
      // Education Details
      education1_c: "",
      education2_c: "",
      education3_c: "",
      education4_c: "",
      education5_c: "",
      educationtype1_c: "",
      educationtype2_c: "",
      educationtype3_c: "",
      educationtype4_c: "",
      educationtype5_c: "",
      institute1_c: "",
      institute2_c: "",
      institute3_c: "",
      institute4_c: "",
      institute5_c: "",
      yearofpassing1_c: "",
      yearofpassing2_c: "",
      yearofpassing3_c: "",
      yearofpassing4_c: "",
      yearofpassing5_c: "",
      // Work Details - Multiple sections (1-5)
      companyname1: "",
      companyname2: "",
      companyname3: "",
      companyname4: "",
      companyname5: "",
      designation1: "",
      designation2: "",
      designation3: "",
      designation4: "",
      designation5: "",
      department1: "",
      department2: "",
      department3: "",
      department4: "",
      department5: "",
      employmenttype1: "",
      employmenttype2: "",
      employmenttype3: "",
      employmenttype4: "",
      employmenttype5: "",
      sector1: "",
      sector2: "",
      sector3: "",
      sector4: "",
      sector5: "",
      description1: "",
      description2: "",
      description3: "",
      description4: "",
      description5: "",
      startdate1: "",
      startdate2: "",
      startdate3: "",
      startdate4: "",
      startdate5: "",
      enddate1: "",
      enddate2: "",
      enddate3: "",
      enddate4: "",
      enddate5: "",
      currentlyworking1: false,
      currentlyworking2: false,
      currentlyworking3: false,
      currentlyworking4: false,
      currentlyworking5: false,
      address1: "",
      address2: "",
      address3: "",
      address4: "",
      address5: "",
      country1: "",
      country2: "",
      country3: "",
      country4: "",
      country5: "",
      state1: "",
      state2: "",
      state3: "",
      state4: "",
      state5: "",
      city1: "",
      city2: "",
      city3: "",
      city4: "",
      city5: "",
      pincode1: "",
      pincode2: "",
      pincode3: "",
      pincode4: "",
      pincode5: "",
      // Legacy work fields
      company_c: "",
      employmenttype_c: "",
      occupation_c: "",
      worklocation_c: "",
    });
    // Reset touched fields when clearing form
    setTouchedFields({
      mobile: false,
      aadhar: false
    });
    // Reset visible sections
    setVisibleEducationSections(1);
    setVisibleWorkSections(1);
    setEducationSectionError("");
    setEducationFieldErrors({});
    setWorkSectionError("");
    setWorkFieldErrors({});
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const toBase64 = (file) => {
    return new Promise((resolve, reject) => {
      if (!file) {
        reject(new Error("No file provided"));
        return;
      }

      // Validate file type
      const validTypes = ["image/jpeg", "image/png", "image/gif"];
      if (!validTypes.includes(file.type)) {
        reject(new Error("Only JPG, PNG, and GIF images are allowed"));
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        try {
          // Ensure the base64 string is properly formatted
          const result = reader.result;
          if (!result.startsWith("data:")) {
            throw new Error("Invalid file format");
          }
          // resolve(result);
          resolve(result.split(",")[1]);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const generateSafeFilename = (originalName) => {
    const timestamp = Date.now();
    const extension = originalName.substring(originalName.lastIndexOf(".")).toLowerCase();
    const baseName = originalName.substring(0, originalName.lastIndexOf("."));

    // Remove special characters and spaces
    const safeName = baseName.replace(/[^a-zA-Z0-9]/g, "_");

    return `${safeName}_${timestamp}${extension}`;
  };
  const postToSuiteCRM = async () => {
    try {
      // Validate required fields: name, gender, relationship type, selected person
      if (!newMember.name || !newMember.gender_c || !newMember.relationshipType || !newMember.selectedPersonId) {
        throw new Error("Please fill in all required fields");
      }

      const hasMobileValue = hasUsablePhoneValue(newMember.mobile);

      if (hasMobileValue && !isValidPhoneNumber(newMember.mobile)) {
        throw new Error("Please enter a valid phone number for the selected country");
      }

      // Validate aadhar number if provided
      if (newMember.aadhar && newMember.aadhar.length !== 12) {
        throw new Error("Aadhaar number must be exactly 12 digits");
      }

      // Determine if editing "Myself" to use the correct module
      const isMySelf = mode === 'edit' && memberData?.displayId === "0";
      
      // Get family group ID from prop if provided, otherwise fallback to localStorage
      const familyGroupId = propFamilyGroupId || localStorage.getItem('familyGroupId') || '';
      
      // Prepare the record data
      // For "Myself", update the Contacts module (user record)
      // For family members, update FT_FamilyTree module
      const recordData = {
        session: sessionId,
        module_name: isMySelf ? "Users" : "FT_FamilyTree",
        name_value_list: [
          { name: "users_id_c", value: user_id },
          { name: "name", value: newMember.name },
          { name: "mobile_c", value: hasMobileValue ? newMember.mobile : "" },
          { name: "email_c", value: newMember.email || "" },
          // CRITICAL: SuiteCRM logic hook requires "relationship" field
          { name: "relationship", value: newMember.relationshipType || "" },
          { name: "relationship_type_c", value: newMember.relationshipType || "" },
          { name: "family_group_id_c", value: familyGroupId },
          { name: "aadhar_c", value: newMember.aadhar },
          { name: "caste_c", value: newMember.caste || "Not specified" },
          { name: "religion_c", value: newMember.religion || "Not specified" },
          { name: "instagram_c", value: newMember.instagram || "" },
          { name: "youtube_c", value: newMember.youtube || "" },
          { name: "facebook_c", value: newMember.facebook || "" },
          { name: "threads_c", value: newMember.threads || "" },
          { name: "linkedin_c", value: newMember.linkedin || "" },
          // Personal Information
          { name: "address1_c", value: newMember.address1_c || "" },
          { name: "address2_c", value: newMember.address2_c || "" },
          { name: "age_c", value: newMember.age_c || "" },
          { name: "alivestatus_c", value: newMember.alivestatus_c || "" },
          { name: "birthplace_c", value: newMember.birthplace_c || "" },
          { name: "bloodgroup_c", value: newMember.bloodgroup_c || "" },
          { name: "dateofdeath_c", value: newMember.dateofdeath_c || "" },
          { name: "deathplace_c", value: newMember.deathplace_c || "" },
          { name: "deathreason_c", value: newMember.deathreason_c || "" },
          { name: "deathaddress_c", value: newMember.deathaddress_c || "" },
          { name: "deatharea_c", value: newMember.deatharea_c || "" },
          { name: "deathpincode_c", value: newMember.deathpincode_c || "" },
          { name: "deathcity_c", value: newMember.deathcity_c || "" },
          { name: "deathstate_c", value: newMember.deathstate_c?.name || "" },
          { name: "deathcountry_c", value: newMember.deathcountry_c?.name || "" },
          { name: "disability_c", value: newMember.disability_c || "" },
          { name: "dob_c", value: newMember.dob_c || "" },
          { name: "gender_c", value: newMember.gender_c || "" },
          { name: "languagesknown_c", value: newMember.languagesknown_c || "" },
          { name: "maritalstatus_c", value: newMember.maritalstatus_c || "" },
          { name: "marriagedate_c", value: newMember.marriagedate_c || "" },
          { name: "marriageplace_c", value: newMember.marriageplace_c || "" },
          { name: "economical_status_c", value: newMember.economical_status_c || "" },
          { name: "mothertongue_c", value: newMember.mothertongue_c || "" },
          // Education Details
          { name: "education1_c", value: newMember.education1_c || "" },
          { name: "education2_c", value: newMember.education2_c || "" },
          { name: "education3_c", value: newMember.education3_c || "" },
          { name: "education4_c", value: newMember.education4_c || "" },
          { name: "education5_c", value: newMember.education5_c || "" },
          { name: "educationtype1_c", value: newMember.educationtype1_c || "" },
          { name: "educationtype2_c", value: newMember.educationtype2_c || "" },
          { name: "educationtype3_c", value: newMember.educationtype3_c || "" },
          { name: "educationtype4_c", value: newMember.educationtype4_c || "" },
          { name: "educationtype5_c", value: newMember.educationtype5_c || "" },
          { name: "institute1_c", value: newMember.institute1_c || "" },
          { name: "institute2_c", value: newMember.institute2_c || "" },
          { name: "institute3_c", value: newMember.institute3_c || "" },
          { name: "institute4_c", value: newMember.institute4_c || "" },
          { name: "institute5_c", value: newMember.institute5_c || "" },
          { name: "yearofpassing1_c", value: newMember.yearofpassing1_c || "" },
          { name: "yearofpassing2_c", value: newMember.yearofpassing2_c || "" },
          { name: "yearofpassing3_c", value: newMember.yearofpassing3_c || "" },
          { name: "yearofpassing4_c", value: newMember.yearofpassing4_c || "" },
          { name: "yearofpassing5_c", value: newMember.yearofpassing5_c || "" },
          // Work Details - Multiple sections (1-5)
          { name: "companyname1", value: newMember.companyname1 || "" },
          { name: "companyname2", value: newMember.companyname2 || "" },
          { name: "companyname3", value: newMember.companyname3 || "" },
          { name: "companyname4", value: newMember.companyname4 || "" },
          { name: "companyname5", value: newMember.companyname5 || "" },
          { name: "designation1", value: newMember.designation1 || "" },
          { name: "designation2", value: newMember.designation2 || "" },
          { name: "designation3", value: newMember.designation3 || "" },
          { name: "designation4", value: newMember.designation4 || "" },
          { name: "designation5", value: newMember.designation5 || "" },
          { name: "department1", value: newMember.department1 || "" },
          { name: "department2", value: newMember.department2 || "" },
          { name: "department3", value: newMember.department3 || "" },
          { name: "department4", value: newMember.department4 || "" },
          { name: "department5", value: newMember.department5 || "" },
          { name: "employmenttype1", value: newMember.employmenttype1 || "" },
          { name: "employmenttype2", value: newMember.employmenttype2 || "" },
          { name: "employmenttype3", value: newMember.employmenttype3 || "" },
          { name: "employmenttype4", value: newMember.employmenttype4 || "" },
          { name: "employmenttype5", value: newMember.employmenttype5 || "" },
          { name: "sector1", value: newMember.sector1 || "" },
          { name: "sector2", value: newMember.sector2 || "" },
          { name: "sector3", value: newMember.sector3 || "" },
          { name: "sector4", value: newMember.sector4 || "" },
          { name: "sector5", value: newMember.sector5 || "" },
          { name: "description1", value: newMember.description1 || "" },
          { name: "description2", value: newMember.description2 || "" },
          { name: "description3", value: newMember.description3 || "" },
          { name: "description4", value: newMember.description4 || "" },
          { name: "description5", value: newMember.description5 || "" },
          { name: "startdate1", value: newMember.startdate1 || "" },
          { name: "startdate2", value: newMember.startdate2 || "" },
          { name: "startdate3", value: newMember.startdate3 || "" },
          { name: "startdate4", value: newMember.startdate4 || "" },
          { name: "startdate5", value: newMember.startdate5 || "" },
          { name: "enddate1", value: newMember.enddate1 || "" },
          { name: "enddate2", value: newMember.enddate2 || "" },
          { name: "enddate3", value: newMember.enddate3 || "" },
          { name: "enddate4", value: newMember.enddate4 || "" },
          { name: "enddate5", value: newMember.enddate5 || "" },
          { name: "currentlyworking1", value: newMember.currentlyworking1 ? "1" : "0" },
          { name: "currentlyworking2", value: newMember.currentlyworking2 ? "1" : "0" },
          { name: "currentlyworking3", value: newMember.currentlyworking3 ? "1" : "0" },
          { name: "currentlyworking4", value: newMember.currentlyworking4 ? "1" : "0" },
          { name: "currentlyworking5", value: newMember.currentlyworking5 ? "1" : "0" },
          { name: "address1", value: newMember.address1 || "" },
          { name: "address2", value: newMember.address2 || "" },
          { name: "address3", value: newMember.address3 || "" },
          { name: "address4", value: newMember.address4 || "" },
          { name: "address5", value: newMember.address5 || "" },
          { name: "country1", value: newMember.country1 || "" },
          { name: "country2", value: newMember.country2 || "" },
          { name: "country3", value: newMember.country3 || "" },
          { name: "country4", value: newMember.country4 || "" },
          { name: "country5", value: newMember.country5 || "" },
          { name: "state1", value: newMember.state1 || "" },
          { name: "state2", value: newMember.state2 || "" },
          { name: "state3", value: newMember.state3 || "" },
          { name: "state4", value: newMember.state4 || "" },
          { name: "state5", value: newMember.state5 || "" },
          { name: "city1", value: newMember.city1 || "" },
          { name: "city2", value: newMember.city2 || "" },
          { name: "city3", value: newMember.city3 || "" },
          { name: "city4", value: newMember.city4 || "" },
          { name: "city5", value: newMember.city5 || "" },
          { name: "pincode1", value: newMember.pincode1 || "" },
          { name: "pincode2", value: newMember.pincode2 || "" },
          { name: "pincode3", value: newMember.pincode3 || "" },
          { name: "pincode4", value: newMember.pincode4 || "" },
          { name: "pincode5", value: newMember.pincode5 || "" },
          // Legacy work fields (keeping for backward compatibility)
          { name: "company_c", value: newMember.company_c || "" },
          { name: "employmenttype_c", value: newMember.employmenttype_c || "" },
          { name: "occupation_c", value: newMember.occupation_c || "" },
          { name: "worklocation_c", value: newMember.worklocation_c || "" },
          // Location - match EditProfileDialog field names
          { name: "villageTown_c", value: newMember.villageTown_c || "" },
          { name: "taluk_c", value: newMember.taluk_c || "" },
          { name: "district_c", value: newMember.district_c || "" },
        ],
      };

      // CRITICAL: Add parentid_c and spouseid_c for SuiteCRM logic hook auto-linking
      // Add parentid_c when relationship is Parent or Child
      if (newMember.relationshipType === 'Parent' || newMember.relationshipType === 'Child') {
        if (newMember.selectedPersonId) {
          recordData.name_value_list.push({
            name: "parentid_c",
            value: newMember.selectedPersonId
          });
        }
      }

      // Add spouseid_c when relationship is Spouse
      if (newMember.relationshipType === 'Spouse') {
        if (newMember.selectedPersonId) {
          recordData.name_value_list.push({
            name: "spouseid_c",
            value: newMember.selectedPersonId
          });
        }
      }

      // In edit mode, include the record ID
      if (mode === 'edit' && memberData?.id) {
        // Use the actual database ID (not "0" for Myself)
        const recordIdToUpdate = memberData.id;
        recordData.name_value_list.unshift({ name: "id", value: recordIdToUpdate });
      }

      console.log('📤 Family Member Payload:', recordData);

      const response = await callFamilyTreeApi("set_entry", recordData);

      if (!response || !response.id) {
        throw new Error(`Failed to ${mode === 'edit' ? 'update' : 'create'} record`);
      }

      const recordId = response.id;
      // Upload image if exists
      if (newMember.photoFile) {
        try {
          const noteData = {
            session: sessionId,
            module_name: "Notes",
            name_value_list: [
              { name: "name", value: "Family Tree Photo" },
              { name: "parent_type", value: "FT_FamilyTree" },
              { name: "parent_id", value: recordId },
            ],
          };

          const noteResponse = await callFamilyTreeApi("set_entry", noteData);

          if (!noteResponse || !noteResponse.id) {
            throw new Error("Failed to create notes record");
          }
          const fileData = await toBase64(newMember.photoFile);
          const fileName = generateSafeFilename(newMember.photoFile.name);

          const uploadData = {
            session: sessionId,
            note: {
              id: noteResponse.id,
              filename: fileName,
              file: fileData,
              file_mime_type: newMember.photoFile.type,
              related_module_name: "FT_FamilyTree",
              related_module_id: recordId,
            },
          };

          await callFamilyTreeApi("set_note_attachment", uploadData);
        } catch (uploadError) {
          console.warn("Image upload failed (record was saved)", uploadError);
          // Continue even if image fails
        }
      }

      // Sync work experience entries to work_work module
      try {
        // Prepare work entries from form data
        const workEntries = [];
        for (let i = 1; i <= visibleWorkSections; i++) {
          const hasData = [
            newMember[`companyname${i}`],
            newMember[`designation${i}`],
            newMember[`employmenttype${i}`],
            newMember[`startdate${i}`],
          ].some((value) => value && String(value).trim() !== "");

          if (hasData) {
            workEntries.push({
              companyname: newMember[`companyname${i}`] || "",
              designation: newMember[`designation${i}`] || "",
              department: newMember[`department${i}`] || "",
              employmenttype: newMember[`employmenttype${i}`] || "",
              sector: newMember[`sector${i}`] || "",
              description: newMember[`description${i}`] || "",
              startdate: newMember[`startdate${i}`] || "",
              enddate: newMember[`enddate${i}`] || "",
              currentlyworking: newMember[`currentlyworking${i}`] ? "1" : "0",
              address: newMember[`address${i}`] || "",
              country: newMember[`country${i}`] || "",
              state: newMember[`state${i}`] || "",
              city: newMember[`city${i}`] || "",
              pincode: newMember[`pincode${i}`] || "",
              name: `Work Experience ${i}`, // Required field for the work module
            });
          }
        }

        // For "Myself", use the logged-in user's ID; otherwise use recordId from FT_FamilyTree
        const isMySelf = newMember.relation?.toLowerCase() === "myself";
        const parentIdForWork = isMySelf ? user_id : recordId;
        
        await syncWorkEntries(sessionId, parentIdForWork, workEntries, []);
      } catch (workError) {
        console.error('❌ Error syncing work experience:', workError);
        // Continue even if work sync fails
      }

      // Sync education entries to education_education module
      try {
        // Prepare education entries from form data
        const educationEntries = [];
        for (let i = 1; i <= visibleEducationSections; i++) {
          const hasData = [
            newMember[`education${i}_c`],
            newMember[`board${i}_c`],
            newMember[`institute${i}_c`],
          ].some((value) => value && String(value).trim() !== "");

          if (hasData) {
            educationEntries.push({
              qualification: newMember[`education${i}_c`] || "",
              board: newMember[`board${i}_c`] || "",
              course: newMember[`course${i}_c`] || "",
              educationtype: newMember[`educationtype${i}_c`] || "Regular",
              instituteschool: newMember[`institute${i}_c`] || "",
              institutecountry: newMember[`institutecountry${i}_c`] || "",
              institutestate: newMember[`institutestate${i}_c`] || "",
              instituecity: newMember[`institutecity${i}_c`] || "",
              institutepincode: newMember[`institutepincode${i}_c`] || "",
              startyear_c: newMember[`startyear${i}_c`] || "",
              currentlystudying_c: newMember[`currentlystudying${i}_c`] ? "1" : "0",
              endyear_c: newMember[`endyear${i}_c`] || "",
              grade: newMember[`grade${i}_c`] || "",
              image: newMember[`educationimage${i}`] || "",
              name: `Education ${i}`, // Required field for the education module
            });
          }
        }

        // For "Myself", use the logged-in user's ID; otherwise use recordId from FT_FamilyTree
        const isMySelf = newMember.relation?.toLowerCase() === "myself";
        const parentIdForEducation = isMySelf ? user_id : recordId;
        
        await syncEducationEntries(sessionId, parentIdForEducation, educationEntries, []);
      } catch (educationError) {
        console.error('❌ Error syncing education:', educationError);
        // Continue even if education sync fails
      }

      // Sync ID card entries to id_idcards module
      try {
        // Prepare ID card entries from form data
        const idCardEntries = [];
        for (let i = 1; i <= visibleIDCardSections; i++) {
          const hasData = [
            newMember[`idtype${i}`],
            newMember[`idcardname${i}`],
            newMember[`idnumber${i}`],
          ].some((value) => value && String(value).trim() !== "");

          if (hasData) {
            const entry = {
              idtype: newMember[`idtype${i}`] || "",
              idcardname: newMember[`idcardname${i}`] || "",
              idnumber: newMember[`idnumber${i}`] || "",
              issuedate: newMember[`issuedate${i}`] || "",
              expirydate: newMember[`expirydate${i}`] || "",
              idcarddescription: newMember[`idcarddescription${i}`] || "",
              name: `ID Card ${i}`, // Required field for the ID card module
            };

            // Include the existing ID if available (for updates)
            if (newMember[`idCardEntryId${i}`]) {
              entry.id = newMember[`idCardEntryId${i}`];
            }

            idCardEntries.push(entry);
          }
        }

        // For "Myself", use the logged-in user's ID; otherwise use recordId from FT_FamilyTree
        const isMySelf = newMember.relation?.toLowerCase() === "myself";
        const parentIdForIDCard = isMySelf ? user_id : recordId;
        
        await syncIDCardEntries(sessionId, parentIdForIDCard, idCardEntries, []);
      } catch (idCardError) {
        console.error('❌ Error syncing ID card:', idCardError);
        // Continue even if ID card sync fails
      }

      // Sync bank details entries to bank_bankdetails module
      try {
        // Prepare bank entries from form data
        const bankEntries = [];
        for (let i = 1; i <= visibleBankSections; i++) {
          const hasData = [
            newMember[`accountholdername${i}`],
            newMember[`accountnumber${i}`],
            newMember[`ifsc${i}`],
            newMember[`bankname${i}`],
          ].some((value) => value && String(value).trim() !== "");

          if (hasData) {
            const entry = {
              accountholdername: newMember[`accountholdername${i}`] || "",
              accountnumber: newMember[`accountnumber${i}`] || "",
              ifsc: newMember[`ifsc${i}`] || "",
              accounttype: newMember[`accounttype${i}`] || "",
              bankname: newMember[`bankname${i}`] || "",
              branch: newMember[`branch${i}`] || "",
              bankcountry: newMember[`bankcountry${i}`] || "",
              status: newMember[`status${i}`] || "Active",
              bankdescription: newMember[`bankdescription${i}`] || "",
              image: newMember[`image${i}`] || "",
              name: `Bank Account ${i}`, // Required field for the bank module
            };

            // Include the existing ID if available (for updates)
            if (newMember[`bankEntryId${i}`]) {
              entry.id = newMember[`bankEntryId${i}`];
            }

            bankEntries.push(entry);
          }
        }

        // For "Myself", use the logged-in user's ID; otherwise use recordId from FT_FamilyTree
        const isMySelf = newMember.relation?.toLowerCase() === "myself";
        const parentIdForBank = isMySelf ? user_id : recordId;
        
        await syncBankDetailsEntries(sessionId, parentIdForBank, bankEntries, []);
      } catch (bankError) {
        console.error('❌ Error syncing bank details:', bankError);
        // Continue even if bank sync fails
      }

      // Sync social links entries to social_sociallinks module
      try {
        // Prepare social links entries from form data
        const socialLinksEntries = [];
        for (let i = 1; i <= visibleSocialLinksSections; i++) {
          const hasData = [
            newMember[`socialtype${i}`],
            newMember[`sociallink${i}`],
          ].some((value) => value && String(value).trim() !== "");

          if (hasData) {
            const entry = {
              type: newMember[`socialtype${i}`] || "", // API expects 'type' not 'socialtype'
              link: newMember[`sociallink${i}`] || "", // API expects 'link' not 'sociallink'
              name: `Social Link ${i}`, // Required field for the social links module
            };

            // Include the existing ID if available (for updates)
            if (newMember[`socialLinksEntryId${i}`]) {
              entry.id = newMember[`socialLinksEntryId${i}`];
            }

            socialLinksEntries.push(entry);
          }
        }

        // For "Myself", use the logged-in user's ID; otherwise use recordId from FT_FamilyTree
        const isMySelf = newMember.relation?.toLowerCase() === "myself";
        const parentIdForSocialLinks = isMySelf ? user_id : recordId;
        
        await syncSocialLinksEntries(sessionId, parentIdForSocialLinks, socialLinksEntries, []);
      } catch (socialLinksError) {
        console.error('❌ Error syncing social links:', socialLinksError);
        // Continue even if social links sync fails
      }

      // Handle local state update based on mode
      if (mode === 'add') {
        const effectiveId = recordId || generateID();
        const newEntry = {
          ...newMember,
          id: effectiveId,
          record_id: recordId || newMember.record_id || effectiveId,
          parent: determineParentId(newMember.relation, familyMembers),
        };
        if (onAddMember) {
          onAddMember(newEntry);
        }
        handleClear();
      } else if (mode === 'edit') {
        const updatedEntry = {
          ...memberData,
          ...newMember,
        };
        if (onUpdateMember) {
          onUpdateMember(updatedEntry);
        }
        onClose();
      }

      return { recordId };
    } catch (error) {
      console.error(`Error ${mode === 'edit' ? 'updating' : 'saving'} to SuiteCRM:`, error);
      setSnackbarMessage(error.message || `Failed to ${mode === 'edit' ? 'update' : 'save'} family member`);
      setErrorSB(true);
      return null;
    }
  };

  const determineParentId = (relation, members) => {
    // Your existing parent determination logic
    if (["Grandfather", "Grandmother"].some((r) => relation.includes(r))) {
      return "ROOT";
    }
    // ... rest of your logic
    return "ROOT"; // fallback
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate required fields: name, gender, relationship type
    if (!newMember.name || !newMember.gender_c || !newMember.relationshipType) {
      setError("Please fill in all required fields");
      return;
    }

    // Validate mobile number using selected country rules (only when provided)
    const hasMobileValue = hasUsablePhoneValue(newMember.mobile);

    if (hasMobileValue && !isValidPhoneNumber(newMember.mobile)) {
      setError("Please enter a valid phone number for the selected country");
      return;
    }

    // Validate aadhar number if provided
    if (newMember.aadhar && newMember.aadhar.length !== 12) {
      setError("Aadhaar number must be exactly 12 digits");
      return;
    }

    // Validate selected person
    if (!newMember.selectedPersonId) {
      handleFieldBlur('selectedPerson');
      setError(`Please select ${selectPersonText.missing}`);
      return;
    }

    // If relationship already confirmed, proceed with API call
    if (relationshipConfirmed) {
      setIsSubmitting(true);
      const result = await postToSuiteCRM();
      setIsSubmitting(false);

      if (result) {
        setSnackbarMessage(`Family member ${mode === 'edit' ? 'updated' : 'saved'} successfully!`);
        setSuccessSB(true);
      }
      return;
    }

    // Show confirmation dialog only if not confirmed yet
    const selectedPerson = selectableFamilyMembers.find(m => m.id === newMember.selectedPersonId);
    const relationshipLabel = newMember.relationshipType || '';
    
    setConfirmDialog({
      open: true,
      data: {
        enteredName: newMember.name,
        relationshipType: relationshipLabel,
        selectedPersonName: selectedPerson?.name || 'Unknown',
      }
    });
  };

  const handleConfirmSave = async () => {
    // Mark relationship as confirmed and close dialog
    setRelationshipConfirmed(true);
    setConfirmDialog({ open: false, data: null });
  };

  const handleCancelConfirm = () => {
    // Reset the selected person and confirmation status when user cancels
    setNewMember((prev) => ({ ...prev, selectedPersonId: '' }));
    setRelationshipConfirmed(false);
    setConfirmDialog({ open: false, data: null });
  };

  const selectableFamilyMembers = useMemo(() => {
    const raw = Array.isArray(familyMembers) ? familyMembers : [];
    const relationshipType = newMember?.relationshipType;
    const selectedId = newMember?.selectedPersonId ? String(newMember.selectedPersonId).trim() : "";

    const currentRecordIds = [];
    if (mode === 'edit') {
      if (memberData?.id) currentRecordIds.push(String(memberData.id).trim());
      if (memberData?.record_id) currentRecordIds.push(String(memberData.record_id).trim());
    }
    if (newMember?.id) currentRecordIds.push(String(newMember.id).trim());

    const normalizedCurrentRecordIds = currentRecordIds.filter(Boolean);

    const parseIds = (value) => {
      if (!value) return [];
      if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean);
      return String(value)
        .replace(/[\[\]"{}]/g, '')
        .split(',')
        .map((v) => v.trim())
        .filter((v) => {
          if (!v) return false;
          const lower = v.toLowerCase();
          return lower !== 'null' && lower !== 'undefined';
        });
    };

    const hasAssignedLink = (value) => parseIds(value).length > 0;

    const isLinkedToCurrentMember = (value) => {
      const ids = parseIds(value);
      if (!ids.length || !normalizedCurrentRecordIds.length) return false;
      return normalizedCurrentRecordIds.some((currId) => ids.includes(currId));
    };

    const inferGender = (genderValue, relationLabel) => {
      const normalized = String(genderValue || '').trim().toLowerCase();
      if (normalized === 'male' || normalized === 'female') return normalized;
      const relation = String(relationLabel || '').trim().toLowerCase();
      const femaleHints = ["wife", "mother", "sister", "daughter", "aunt", "grandmother", "niece", "female"];
      if (femaleHints.some((hint) => relation.includes(hint))) return "female";
      const maleHints = ["husband", "father", "brother", "son", "uncle", "grandfather", "nephew", "male"];
      if (maleHints.some((hint) => relation.includes(hint))) return "male";
      return "";
    };

    let subjectGender = inferGender(newMember?.gender_c, newMember?.relation);
    if (!subjectGender && mode === 'edit') {
      subjectGender = inferGender(memberData?.gender_c, memberData?.relation);
    }

    // Special handling: for Child, show couples only as selectable parents
    if (relationshipType === 'Child') {
      const byId = new Map();
      raw.forEach(m => { if (m?.id) byId.set(String(m.id).trim(), m); });
      const couples = new Map();
      raw.forEach(a => {
        const spouseId = a?.spouseId || a?.spouse_id || a?.spouseid_c || null;
        if (!spouseId) return;
        const b = byId.get(String(spouseId).trim());
        if (!b) return;
        const ida = String(a.id).trim();
        const idb = String(b.id).trim();
        const key = ida < idb ? `${ida}_${idb}` : `${idb}_${ida}`;
        if (couples.has(key)) return;
        const ga = String(a.gender || a.gender_c || '').toLowerCase();
        const gb = String(b.gender || b.gender_c || '').toLowerCase();
        const male = ga === 'male' ? a : gb === 'male' ? b : a;
        const female = male === a ? b : a;
        const label = `${male?.name || male?.fullName || male?.firstName || ida} - ${female?.name || female?.fullName || female?.firstName || idb}`;
        couples.set(key, {
          id: male?.id, // selecting sets parent to male id
          couple: true,
          maleId: male?.id,
          femaleId: female?.id,
          name: label,
        });
      });
      return Array.from(couples.values());
    }

    const filtered = raw.filter((member) => {
      if (!member) return false;
      const idStr = String(member.id ?? '').trim();
      if (!idStr) return false;
      // Only exclude the user login placeholder with id '0', allow family tree 'Myself' record
      if (idStr === '0') return false;
      if (newMember?.id && idStr === String(newMember.id).trim()) return false;

      const isSelected = selectedId && idStr === selectedId;

      if (relationshipType === 'Parent') {
        // When relationship is "Parent", we're saying "this person is parent OF someone"
        // So we should only show people who DON'T already have a parent assigned
        const parentsValue =
          member.parentid_c ??
          member.parentsid_c ??
          member.parent_id ??
          member.parentId ??
          member.parentsIds ??
          member.parent ??
          '';
        
        // Exclude if this member already has a parent assigned (unless it's the current member being edited)
        const hasParentAssigned = parentsValue !== '' && 
                                  parentsValue !== null && 
                                  parentsValue !== undefined &&
                                  String(parentsValue).trim() !== '' &&
                                  String(parentsValue).trim().toLowerCase() !== 'null' &&
                                  String(parentsValue).trim().toLowerCase() !== 'undefined';
        
        const linkedToCurrent = hasParentAssigned && isLinkedToCurrentMember(parentsValue);
        
        // Exclude if: has parent AND (not linked to current member AND not currently selected)
        if (hasParentAssigned && !linkedToCurrent && !isSelected) {
          return false;
        }
      } else if (relationshipType === 'Spouse') {
        const spouseValue =
          member.spouseid_c ??
          member.spouse_id ??
          member.spouseId ??
          member.husbandid_c ??
          member.wifeid_c ??
          '';
        const hasSpouse = hasAssignedLink(spouseValue);
        const linkedToCurrent = isLinkedToCurrentMember(spouseValue) ||
          (newMember?.id && parseIds(spouseValue).includes(String(newMember.id).trim()));

        const candidateGender = inferGender(member.gender_c, member.relationship);

        // Must be opposite gender
        if (subjectGender && candidateGender && subjectGender === candidateGender) {
          // allow currently selected even if same due to edit context
          if (!isSelected) return false;
        }

        // Must not already have a spouse unless it's linked to the current member (editing existing spouse)
        if (hasSpouse && !linkedToCurrent) {
          if (!isSelected) return false;
        }
      }

      return true;
    });

    return filtered;
  }, [
    familyMembers,
    mode,
    memberData?.id,
    memberData?.record_id,
    memberData?.relation,
    memberData?.gender_c,
    newMember?.id,
    newMember?.relation,
    newMember?.gender_c,
    newMember?.relationshipType,
    newMember?.selectedPersonId,
  ]);

  const selectPersonText = useMemo(() => {
    switch (newMember.relationshipType) {
      case "Parent":
        return {
          label: "This person is parent of",
          placeholder: "Select child",
          missing: "a child",
        };
      case "Spouse":
        return {
          label: "This person is spouse of",
          placeholder: "Select spouse",
          missing: "a spouse",
        };
      case "Child":
        return {
          label: "This person is child of",
          placeholder: "Select parent",
          missing: "a parent",
        };
      default:
        return {
          label: "Select Person",
          placeholder: "Select family member",
          missing: "a family member",
        };
    }
  }, [newMember.relationshipType]);

  const showSelectPersonError = touchedFields.selectedPerson && !newMember.selectedPersonId;

  return (
    <>
      <style>{`
        .PhoneInputInput {
          height: 56px !important;
          border: 1px solid #ccc !important;
          border-radius: 4px !important;
          padding: 0 14px !important;
          font-size: 16px !important;
        }
        .PhoneInputInput:focus {
          border-color: #1976d2 !important;
          box-shadow: 0 0 0 1px #1976d2 !important;
          outline: none;
        }
        .phone-input-error .PhoneInputInput {
          border-color: #ef4444 !important;
          box-shadow: 0 0 0 1px rgba(239, 68, 68, 0.15) !important;
        }
        .phone-input-wrapper {
          display: flex;
          gap: 8px;
        }
        .PhoneInputCountry {
          flex: 1 1 0;
          min-width: 0;
        }
        .PhoneInputCountrySelect {
          font-size: 14px !important;
        }
        .PhoneInputInput {
          flex: 1 1 0;
          min-width: 0;
        }
        /* Global MUI TextField height for all fields */
        .MuiInputBase-root {
          height: 56px !important;
        }
        .MuiOutlinedInput-input {
          padding: 0 14px !important;
        }
      `}</style>
      <div className="bg-white rounded-lg shadow-xl p-8 mb-8">
      {/* <h2 className="text-2xl font-semibold text-center text-gray-700 mb-6">Add Family Member</h2> */}

      {error && <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg">{error}</div>}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Photo Upload Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
          <label className="h-52 w-full flex flex-col items-center justify-center gap-4 cursor-pointer border-2 border-dashed border-gray-300 bg-white p-6 rounded-lg shadow-md">
            <div className="flex justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-20 w-20 text-gray-600"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 1C9.73 1 9.48 1.11 9.29 1.29L3.29 7.29C3.11 7.48 3 7.73 3 8V20C3 21.66 4.34 23 6 23H7C7.55 23 8 22.55 8 22C8 21.45 7.55 21 7 21H6C5.45 21 5 20.55 5 20V9H10C10.55 9 11 8.55 11 8V3H18C18.55 3 19 3.45 19 4V9C19 9.55 19.45 10 20 10C20.55 10 21 9.55 21 9V4C21 2.34 19.66 1 18 1H10ZM9 7H6.41L9 4.41V7ZM14 15.5C14 14.12 15.12 13 16.5 13C17.88 13 19 14.12 19 15.5V16V17H20C21.1 17 22 17.9 22 19C22 20.1 21.1 21 20 21H13C11.9 21 11 20.1 11 19C11 17.9 11.9 17 13 17H14V16V15.5ZM16.5 11C14.14 11 12.21 12.81 12.02 15.12C10.28 15.56 9 17.13 9 19C9 21.21 10.79 23 13 23H20C22.21 23 24 21.21 24 19C24 17.13 22.72 15.56 20.98 15.12C20.79 12.81 18.86 11 16.5 11Z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="text-center">
              <span className="text-gray-600 font-medium">Click to upload image</span>
              <p className="text-sm text-gray-500 mt-1">Max 15MB</p>
            </div>
            <input
              id="add-family-photo-input"
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              className="hidden"
              ref={fileInputRef}
            />
          </label>

          <div className="text-center">
            <p className="text-gray-600 mb-2">Profile Preview:</p>
            <img
              src={newMember.photo || Male}
              alt="Profile Preview"
              className="w-32 h-32 object-cover rounded-full mx-auto border max-h-32"
            />
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              <Button
                variant="outlined"
                size="small"
                sx={{
                  color: '#1976d2',
                  borderColor: '#1976d2',
                  backgroundColor: '#fff',
                  fontWeight: '500',
                  textTransform: 'none',
                  paddingX: 2.5,
                  paddingY: 1,
                  borderRadius: '0.75rem',
                  '&:hover': {
                    backgroundColor: '#e3f2fd',
                    borderColor: '#1565c0',
                    color: '#1565c0'
                  }
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                Choose Photo
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<CameraAltIcon />}
                onClick={handleOpenCamera}
                sx={{
                  color: '#1976d2',
                  borderColor: '#1976d2',
                  backgroundColor: '#fff',
                  fontWeight: '500',
                  textTransform: 'none',
                  paddingX: 2.5,
                  paddingY: 1,
                  borderRadius: '0.75rem',
                  '&:hover': {
                    backgroundColor: '#e3f2fd',
                    borderColor: '#1565c0',
                    color: '#1565c0'
                  }
                }}
              >
                Take Photo
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
          {/* Name Field */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              id="name"
              type="text"
              name="name"
              value={newMember.name}
              onChange={handleInputChange}
              placeholder="Enter full name"
              className="w-full h-14 p-3 border border-gray-300 rounded-md shadow focus:outline-none focus:ring-1 focus:ring-blue-600 text-base"
              required
            />
          </div>

          {/* Mobile Field */}
          <Grid item xs={12} sm={6}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mobile (Optional)
            </label>
            <PhoneInput
              international
              withCountryCallingCode
              placeholder="Enter phone number"
              value={newMember.mobile}
              onChange={(value) => setNewMember((prev) => ({ ...prev, mobile: value }))}
              country={newMember.phoneCountry || "IN"}
              onCountryChange={(country) => setNewMember((prev) => ({ ...prev, phoneCountry: country || "IN" }))}
              onBlur={() => handleFieldBlur("mobile")}
              labels={phoneLabels}
              countrySelectComponent={PhoneCountryAutocomplete}
              countrySelectProps={{
                textFieldProps: {
                  size: "medium",
                  label: "Country",
                  placeholder: "Search country",
                },
                sx: {
                  width: "50%",
                  minWidth: "140px",
                  mr: 1,
                  "& .MuiInputBase-root": {
                    height: "56px",
                    fontSize: "14px",
                  },
                },
              }}
              className={`phone-input-wrapper w-full ${
                touchedFields.mobile && hasUsablePhoneValue(newMember.mobile) && !isValidPhoneNumber(newMember.mobile || "")
                  ? "phone-input-error"
                  : ""
              }`}
              readOnly={mode === 'edit' && memberData?.id === '0'}
            />
            {touchedFields.mobile && hasUsablePhoneValue(newMember.mobile) && !isValidPhoneNumber(newMember.mobile || "") && (
              <p className="mt-1 text-xs text-red-500">
                Enter a valid phone number for the selected country
              </p>
            )}
          </Grid>

          {/* Email Field */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={newMember.email}
              onChange={handleInputChange}
              readOnly={mode === 'edit' && memberData?.id === '0'}
              placeholder="Enter email address"
              className="w-full h-14 p-3 border border-gray-300 rounded-md shadow focus:outline-none focus:ring-1 focus:ring-blue-600 text-base"
            />
          </div>

          {/* Aadhaar Field */}
          <div>
            <label htmlFor="aadhar" className="block text-sm font-medium text-gray-700 mb-1">
              Aadhaar
            </label>
            <input
              id="aadhar"
              type="text"
              name="aadhar"
              value={newMember.aadhar}
              onChange={handleInputChange}
              onBlur={() => handleFieldBlur('aadhar')}
              pattern="[0-9]{12}"
               placeholder="Enter 12-digit Aadhaar number"
              inputMode="numeric"
              minLength={12}
              maxLength={12}
              className={`w-full h-14 p-3 border ${
                touchedFields.aadhar && newMember.aadhar && newMember.aadhar.length !== 12
                  ? "border-red-500"
                  : "border-gray-300"
              } rounded-md shadow focus:outline-none focus:ring-1 focus:ring-blue-600 text-base`}
              title="Aadhaar number must be exactly 12 digits"
            />
            {touchedFields.aadhar && newMember.aadhar && newMember.aadhar.length !== 12 ? (
              <p className="mt-1 text-xs text-red-500">Aadhaar number must be exactly 12 digits</p>
            ) : (
              <div className="h-5">{/* Empty space holder to maintain layout */}</div>
            )}
          </div>

          {/* Gender Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Gender <span className="text-red-500">*</span>
            </label>
            <Autocomplete
              options={["Male", "Female", "Other"]}
              value={newMember.gender_c || null}
              onChange={(e, newValue) => setNewMember(prev => ({ ...prev, gender_c: newValue || "" }))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  fullWidth
                  variant="outlined"
                  placeholder="Select gender"
                  sx={{
                    "& .MuiInputBase-root": {
                      height: "56px",
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: "0 14px",
                    },
                  }}
                />
              )}
            />
          </div>

          {/* Relation Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Relationship Type <span className="text-red-500">*</span>
            </label>
            <Autocomplete
              options={['Parent', 'Spouse', 'Child']}
              getOptionLabel={(option) => option}
              value={newMember.relationshipType || null}
              onChange={(event, newValue) => {
                setNewMember((prev) => ({ ...prev, relationshipType: newValue || '' }));
              }}
              disabled={mode === 'edit' && memberData?.displayId === "0"}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  fullWidth
                  variant="outlined"
                  placeholder="Select relationship type"
                  helperText={mode === 'edit' && memberData?.displayId === "0" ? 'Relationship cannot be changed for your own profile' : ' '}
                  sx={{
                    "& .MuiInputBase-root": {
                      height: "56px",
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: "0 14px",
                    },
                  }}
                />
              )}
            />
          </div>

          {/* Select Person Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {selectPersonText.label} <span className="text-red-500">*</span>
            </label>
            <Autocomplete
              options={selectableFamilyMembers}
              getOptionLabel={(option) => option?.name || option?.firstName || option?.fullName || option?.id || ''}
              noOptionsText="No family members available"
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.name || option.firstName || option.fullName || option.id || 'Unnamed'}
                </li>
              )}
              value={selectableFamilyMembers.find(m => m.id === newMember.selectedPersonId) || null}
              onChange={(event, newValue) => {
                const selectedPersonId = newValue?.couple ? (newValue?.maleId || '') : (newValue?.id || '');
                setNewMember((prev) => ({ ...prev, selectedPersonId }));
                setTouchedFields((prev) => ({ ...prev, selectedPerson: false }));
                // Reset confirmation status when person changes
                setRelationshipConfirmed(false);
                
                // Show confirmation dialog when person is selected (and relationship type exists)
                if (selectedPersonId && newMember.relationshipType && newMember.name) {
                  const relationshipLabel = newMember.relationshipType;
                  setConfirmDialog({
                    open: true,
                    data: {
                      enteredName: newMember.name,
                      relationshipType: relationshipLabel,
                      selectedPersonName: newValue?.name || 'Unknown',
                    }
                  });
                }
              }}
              onBlur={() => handleFieldBlur('selectedPerson')}
              disabled={mode === 'edit' && memberData?.displayId === "0"}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  fullWidth
                  variant="outlined"
                  placeholder={selectPersonText.placeholder}
                  error={showSelectPersonError}
                  helperText={showSelectPersonError
                    ? `Please select ${selectPersonText.missing}`
                    : (mode === 'edit' && memberData?.displayId === "0"
                      ? 'Person cannot be changed for your own profile'
                      : ' ')}
                  sx={{
                    "& .MuiInputBase-root": {
                      height: "56px",
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: "0 14px",
                    },
                  }}
                />
              )}
            />
            {newMember.relationshipType === 'Child' && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                If your parent name is not listed, add their spouse first.
              </Typography>
            )}
          </div>

          {/* Caste Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Caste
            </label>
            <Autocomplete
              options={castes.map(item => item.name)}
              getOptionLabel={(option) => option}
              value={newMember.caste}
              onChange={handleCasteChange}
              onInputChange={handleCasteInputChange}
              freeSolo
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  variant="outlined"
                  placeholder="Select or enter caste"
                  error={casteInvalid}
                  helperText={casteInvalid ? 'Value not in list. Click "Add caste" below to add it or clear the field.' : ' '}
                  sx={{
                    "& .MuiInputBase-root": {
                      height: "56px",
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: "0 14px",
                    },
                  }}
                />
              )}
            />
            {shouldOfferAddCaste && (
              <Box sx={{ mt: 1 }}>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => openAddMasterDialog("Caste", newMember.caste)}
                >
                  Add caste "{newMember.caste}"
                </Button>
              </Box>
            )}
          </div>

          {/* Religion Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Religion
            </label>
            <Autocomplete
              options={religions.map(item => item.name)}
              getOptionLabel={(option) => option}
              value={newMember.religion}
              onChange={handleReligionChange}
              onInputChange={handleReligionInputChange}
              freeSolo
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  variant="outlined"
                  placeholder="Select or enter religion"
                  error={religionInvalid}
                  helperText={religionInvalid ? 'Value not in list. Click "Add religion" below to add it or clear the field.' : ' '}
                  sx={{
                    "& .MuiInputBase-root": {
                      height: "56px",
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: "0 14px",
                    },
                  }}
                />
              )}
            />
            {shouldOfferAddReligion && (
              <Box sx={{ mt: 1 }}>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => openAddMasterDialog("Religion", newMember.religion)}
                >
                  Add religion "{newMember.religion}"
                </Button>
              </Box>
            )}
          </div>

          {/* Address fields were moved to a dedicated section placed after Language Details */}
        </div>

        {/* New Form Fields Container - Scoped Styling */}
        <div className="family-form-fields">
        {/* 🧍‍♂️ 1. Personal Details Section */}
        <div className="mt-6 mb-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b-2 border-blue-500 pb-2">🧍‍♂️ 1. Personal Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Date of Birth */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                <TextField
                  fullWidth
                  type="date"
                  name="dob_c"
                  value={newMember.dob_c}
                  onChange={handleInputChange}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                />
              </div>

              {/* Age */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                <TextField
                  fullWidth
                  type="number"
                  name="age_c"
                  value={newMember.age_c}
                  onChange={handleInputChange}
                  variant="outlined"
                  placeholder="Enter age"
                />
              </div>

              {/* Blood Group */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Blood Group</label>
                <Autocomplete
                  options={["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-", "Prefer not to disclose"]}
                  freeSolo
                  selectOnFocus
                  handleHomeEndKeys
                  value={newMember.bloodgroup_c || ""}
                  onChange={(e, newValue) =>
                    setNewMember((prev) => ({ ...prev, bloodgroup_c: newValue || "" }))
                  }
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === "input") {
                      setNewMember((prev) => ({ ...prev, bloodgroup_c: newInputValue }));
                    }
                    if (reason === "clear") {
                      setNewMember((prev) => ({ ...prev, bloodgroup_c: "" }));
                    }
                  }}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select or enter blood group" />
                  )}
                />
              </div>

              {/* Birth Place */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Birth Place</label>
                <TextField
                  fullWidth
                  name="birthplace_c"
                  value={newMember.birthplace_c}
                  onChange={handleInputChange}
                  variant="outlined"
                  placeholder="Enter birth place"
                />
              </div>

              {/* Disability */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Disability</label>
                <Autocomplete
                  options={["Yes", "No", "Prefer not to disclose"]}
                  value={newMember.disability_c || null}
                  onChange={(e, newValue) => setNewMember(prev => ({ ...prev, disability_c: newValue || "" }))}
                  renderInput={(params) => <TextField {...params} placeholder="Select disability status" />}
                />
              </div>

              {/* Alive Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Alive Status</label>
                <Autocomplete
                  options={["Alive", "Deceased / Passed away / No longer living / Rest in peace"]}
                  value={newMember.alivestatus_c || "Alive"}
                  onChange={(e, newValue) => setNewMember(prev => ({ ...prev, alivestatus_c: newValue || "Alive" }))}
                  renderInput={(params) => <TextField {...params} placeholder="Select alive status" />}
                />
              </div>

              {/* Marital Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Marital Status</label>
                <Autocomplete
                  options={["Single / Unmarried / Spinster", "Married", "Divorced", "Widowed", "Prefer not to disclose"]}
                  value={newMember.maritalstatus_c || null}
                  onChange={(e, newValue) => setNewMember(prev => ({ ...prev, maritalstatus_c: newValue || "" }))}
                  renderInput={(params) => <TextField {...params} placeholder="Select marital status" />}
                />
              </div>

              {/* Economic Status - moved to Personal Details section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Economic Status</label>
                <Autocomplete
                  options={[
                    "Poor/Lower Income",
                    "Lower Middle Class",
                    "Middle Class",
                    "Upper Middle Class",
                    "Rich/Affluent",
                    "Ultra Rich/Wealthy"
                  ]}
                  value={newMember.economical_status_c || null}
                  onChange={(e, newValue) => setNewMember(prev => ({ ...prev, economical_status_c: newValue || "" }))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select economic status"
                      variant="outlined"
                    />
                  )}
                />
              </div>

              {/* Marriage-related fields - only show when Married, Divorced, or Widowed is selected */}
              {(newMember.maritalstatus_c === "Married" || newMember.maritalstatus_c === "Divorced" || newMember.maritalstatus_c === "Widowed") && (
                <>
                  {/* Marriage Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Marriage Date</label>
                    <TextField
                      fullWidth
                      type="date"
                      name="marriagedate_c"
                      value={newMember.marriagedate_c}
                      onChange={handleInputChange}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                    />
                  </div>

                  {/* Marriage Place */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Marriage Place</label>
                    <TextField
                      fullWidth
                      name="marriageplace_c"
                      value={newMember.marriageplace_c}
                      onChange={handleInputChange}
                      variant="outlined"
                      placeholder="Enter marriage place"
                    />
                  </div>
                </>
              )}

              {/* Death-related fields - only show when Deceased is selected */}
              {newMember.alivestatus_c === "Deceased / Passed away / No longer living / Rest in peace" && (
                <>
                  {/* Date of Death */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date of Death</label>
                    <TextField
                      fullWidth
                      type="date"
                      name="dateofdeath_c"
                      value={newMember.dateofdeath_c}
                      onChange={handleInputChange}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                    />
                  </div>

                  {/* Death Place */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Death Place</label>
                    <TextField
                      fullWidth
                      name="deathplace_c"
                      value={newMember.deathplace_c}
                      onChange={handleInputChange}
                      variant="outlined"
                      placeholder="Enter death place"
                    />
                  </div>

                  {/* Death Reason */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Death Reason</label>
                    <TextField
                      fullWidth
                      name="deathreason_c"
                      value={newMember.deathreason_c}
                      onChange={handleInputChange}
                      variant="outlined"
                      placeholder="Enter death reason"
                    />
                  </div>

                  {/* Death Address */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Death Address</label>
                    <TextField
                      fullWidth
                      name="deathaddress_c"
                      value={newMember.deathaddress_c}
                      onChange={handleInputChange}
                      variant="outlined"
                      placeholder="Enter death address"
                    />
                  </div>

                  {/* Death Pincode - Moved above Death Area */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Death Pincode</label>
                    <TextField
                      fullWidth
                      name="deathpincode_c"
                      value={newMember.deathpincode_c}
                      onChange={(e) => {
                        const value = e.target.value;
                        handleInputChange(e);
                        // Trigger autofill when pincode is 6 digits for India
                        if (value.length === 6 && /^\d{6}$/.test(value)) {
                          handleDeathPincodeInput(value);
                        }
                      }}
                      variant="outlined"
                      placeholder="Enter death pincode to auto-populate location"
                      inputProps={{
                        maxLength: 6,
                        pattern: "[0-9]*",
                        inputMode: "numeric",
                      }}
                      helperText={
                        deathPincodeLoading ? (
                          <span style={{ color: '#1976d2' }}>🔄 Fetching location details...</span>
                        ) : deathPincodeError ? (
                          <span style={{ color: '#d32f2f' }}>⚠️ {deathPincodeError}</span>
                        ) : (
                          "Enter 6-digit pincode to auto-populate area, city, state"
                        )
                      }
                    />
                  </div>

                  {/* Death Area - Now with dropdown options from pincode */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Death Area
                      {deathAreaOptions.length > 0 && (
                        <span style={{ fontSize: '12px', color: '#1976d2', marginLeft: '8px' }}>
                          ({deathAreaOptions.length} options from pincode)
                        </span>
                      )}
                    </label>
                    <Autocomplete
                      fullWidth
                      options={deathAreaOptions}
                      getOptionLabel={(option) => option}
                      value={newMember.deatharea_c || null}
                      onChange={(e, value) => setNewMember((prev) => ({ ...prev, deatharea_c: value || "" }))}
                      onInputChange={(e, value) => {
                        if (e?.type === "change") {
                          setNewMember((prev) => ({ ...prev, deatharea_c: value || "" }));
                        }
                      }}
                      freeSolo
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder={deathAreaOptions.length > 0 ? "Select or enter death area" : "Enter death area"}
                          helperText={
                            deathAreaOptions.length > 0
                              ? `${deathAreaOptions.length} areas available from pincode`
                              : "Enter death pincode to see area options"
                          }
                        />
                      )}
                    />
                  </div>

                  {/* Death City */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Death City</label>
                    <Autocomplete
                      options={CITIES}
                      getOptionLabel={(option) => option}
                      value={newMember.deathcity_c || null}
                      onChange={(e, value) => setNewMember(prev => ({ ...prev, deathcity_c: value || "" }))}
                      onInputChange={(e, value) => {
                        if (e?.type === "change") {
                          setNewMember(prev => ({ ...prev, deathcity_c: value || "" }));
                        }
                      }}
                      freeSolo
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder="Select or enter death city"
                        />
                      )}
                    />
                  </div>

                  {/* Death State */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Death State</label>
                    <Autocomplete
                      options={
                        newMember.deathcountry_c
                          ? allStates.filter((state) => state.countryCode === newMember.deathcountry_c.isoCode)
                          : allStates
                      }
                      getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
                      value={newMember.deathstate_c || null}
                      onChange={(e, value) => setNewMember(prev => ({ ...prev, deathstate_c: value || null }))}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder="Select death state"
                        />
                      )}
                      disabled={!newMember.deathcountry_c}
                    />
                  </div>

                  {/* Death Country */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Death Country</label>
                    <Autocomplete
                      options={allCountries}
                      getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
                      value={newMember.deathcountry_c || null}
                      onChange={(e, value) => {
                        setNewMember(prev => ({ ...prev, deathcountry_c: value || null }));
                        // Clear state when country changes
                        if (value?.isoCode !== newMember.deathcountry_c?.isoCode) {
                          setNewMember(prev => ({ ...prev, deathstate_c: null }));
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder="Select death country"
                        />
                      )}
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* 🗣️ 2. Language Details Section */}
          <div className="mt-6 mb-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b-2 border-green-500 pb-2">🗣️ 2. Language Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Mother Tongue */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mother Tongue</label>
                <Autocomplete
                  options={LANGUAGES}
                  value={newMember.mothertongue_c || null}
                  onChange={(e, newValue) => setNewMember(prev => ({ ...prev, mothertongue_c: newValue || "" }))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select mother tongue"
                      variant="outlined"
                    />
                  )}
                />
              </div>

              {/* Languages Known */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Languages Known</label>
                <Autocomplete
                  multiple
                  options={LANGUAGES}
                  value={newMember.languagesknown_c ? (typeof newMember.languagesknown_c === 'string' ? newMember.languagesknown_c.split(', ') : newMember.languagesknown_c) : []}
                  onChange={(e, newValue) => setNewMember(prev => ({ ...prev, languagesknown_c: newValue.join(', ') }))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select languages"
                      variant="outlined"
                      sx={{
                        '& .MuiInputBase-root': {
                          height: 'auto !important',
                          minHeight: '56px',
                          padding: '8px 14px',
                          alignItems: 'flex-start',
                          flexWrap: 'wrap',
                        },
                        '& .MuiOutlinedInput-input': {
                          padding: '0 !important',
                          minWidth: '30px',
                        },
                        '& .MuiAutocomplete-endAdornment': {
                          position: 'relative',
                          top: 'auto',
                          right: 'auto',
                          marginTop: '4px',
                        },
                      }}
                    />
                  )}
                  sx={{
                    '& .MuiAutocomplete-tag': {
                      margin: '2px',
                      maxWidth: '100%',
                    },
                  }}
                />
              </div>
            </div>
          </div>

          {/* 🏠 Address Details Section (moved here — shown after Languages) */}
          <div className="mt-6 mb-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b-2 border-blue-500 pb-2">🏠 Address Details</h3>
            <div className="mb-4">
              <h4 className="text-md font-semibold text-gray-700 mb-3">Current Address</h4>
              {(() => {
                const mapped = {
                  // current prefix
                  current_address1: newMember.current_address1 || newMember.address || "",
                  current_street1: newMember.current_street1 || newMember.address2_c || "",
                  current_pincode1: newMember.current_pincode1 || newMember.pincode || "",
                  current_village1: newMember.current_village1 || newMember.city || newMember.villageTown_c || "",
                  current_taluk1: newMember.current_taluk1 || newMember.taluk_c || "",
                  current_district1: newMember.current_district1 || newMember.district_c || "",
                  current_state1: newMember.current_state1 || newMember.state || "",
                  current_country1: newMember.current_country1 || newMember.country || "",
                  // permanent prefix (kept so AddressFields has all keys)
                  permanent_address1: newMember.permanent_address1 || "",
                  permanent_street1: newMember.permanent_street1 || "",
                  permanent_pincode1: newMember.permanent_pincode1 || "",
                  permanent_village1: newMember.permanent_village1 || "",
                  permanent_taluk1: newMember.permanent_taluk1 || "",
                  permanent_district1: newMember.permanent_district1 || "",
                  permanent_state1: newMember.permanent_state1 || "",
                  permanent_country1: newMember.permanent_country1 || "",
                };

                return (
                  <AddressFields
                    formData={mapped}
                    handleChange={handleAddressFieldChange}
                    addressType="Current"
                    section={1}
                    textInputStyle={{}}
                    fieldErrors={{}}
                  />
                );
              })()}
            </div>

            <div className="mt-4">
              <h4 className="text-md font-semibold text-gray-700 mb-2">Permanent Address</h4>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sameAsCurrent}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setSameAsCurrent(checked);
                      if (checked) {
                        // Copy current mapped values into permanent_* keys
                        const currentFields = [
                          "current_address1",
                          "current_street1",
                          "current_pincode1",
                          "current_village1",
                          "current_taluk1",
                          "current_district1",
                          "current_state1",
                          "current_country1",
                        ];
                        const permanentUpdates = {};
                        currentFields.forEach((field) => {
                          const perm = field.replace("current_", "permanent_");
                          permanentUpdates[perm] = newMember[field] || newMember[field.replace("current_", "")] || "";
                        });
                        setNewMember((prev) => ({ ...prev, ...permanentUpdates }));
                      }
                    }}
                    sx={{ color: "#1976d2", '&.Mui-checked': { color: '#1976d2' } }}
                  />
                }
                label="Same as Current Address"
                sx={{ mb: 2 }}
              />

              {(() => {
                const mappedPerm = {
                  permanent_address1: newMember.permanent_address1 || "",
                  permanent_street1: newMember.permanent_street1 || "",
                  permanent_pincode1: newMember.permanent_pincode1 || "",
                  permanent_village1: newMember.permanent_village1 || "",
                  permanent_taluk1: newMember.permanent_taluk1 || "",
                  permanent_district1: newMember.permanent_district1 || "",
                  permanent_state1: newMember.permanent_state1 || "",
                  permanent_country1: newMember.permanent_country1 || "",
                };

                return (
                  <AddressFields
                    formData={mappedPerm}
                    handleChange={handleAddressFieldChange}
                    addressType="Permanent"
                    section={1}
                    fieldErrors={{}}
                    isSameAsCurrent={sameAsCurrent}
                    onSameAsCurrentChange={setSameAsCurrent}
                  />
                );
              })()}
            </div>
          </div>

          {/* 🎓 3. Education Details Section */}
          <div className="mt-6 mb-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b-2 border-purple-500 pb-2">🎓 3. Education Details</h3>
            <div className="space-y-6">
              {Array.from({ length: visibleEducationSections }, (_, index) => {
                const section = index + 1;
                
                // Create a mapped formData object that EducationFields can read
                // Map FamilyMemberDialog field names to EducationFields field names
                const mappedFormData = {
                  ...newMember,
                  [`qualification${section}`]: newMember[`education${section}_c`] || "",
                  [`board${section}`]: newMember[`board${section}_c`] || "",
                  [`course${section}`]: newMember[`course${section}_c`] || "",
                  [`educationtype${section}`]: newMember[`educationtype${section}_c`] || "",
                  [`instituteschool${section}`]: newMember[`institute${section}_c`] || "",
                  [`institutecountry${section}`]: newMember[`institutecountry${section}_c`] || "",
                  [`institutestate${section}`]: newMember[`institutestate${section}_c`] || "",
                  [`instituecity${section}`]: newMember[`institutecity${section}_c`] || "",
                  [`institutepincode${section}`]: newMember[`institutepincode${section}_c`] || "",
                  [`startyear_c${section}`]: newMember[`startyear${section}_c`] || "",
                  [`currentlystudying_c${section}`]: newMember[`currentlystudying${section}_c`] || false,
                  [`endyear_c${section}`]: newMember[`endyear${section}_c`] || "",
                  [`grade${section}`]: newMember[`grade${section}_c`] || "",
                  [`educationimage${section}`]: newMember[`educationimage${section}`] || "",
                };
                
                return (
                  <div key={section} className="border-l-4 border-green-300 pl-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-md font-semibold text-gray-700">Education {section}</h4>
                      {section > 1 && (
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          onClick={() => handleRemoveEducationSection(section)}
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            color: "#d32f2f",
                            backgroundColor: "rgba(211,47,47,0.12)",
                            '&:hover': {
                              backgroundColor: "rgba(211,47,47,0.2)",
                            },
                          }}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                    <EducationFields
                      formData={mappedFormData}
                      handleChange={handleEducationFieldChange}
                      boards={boards}
                      courses={courses}
                      degreeTypes={degreeTypes}
                      institutes={institutes}
                      countries={allCountries}
                      states={allStates}
                      cities={CITIES}
                      section={section}
                      textInputStyle={{}}
                      fieldErrors={educationFieldErrors}
                    />
                  </div>
                );
              })}
              
              {/* Education section error message */}
              {educationSectionError && (
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                  {educationSectionError}
                </Typography>
              )}

              {/* Add Education button */}
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddEducationSection}
                disabled={visibleEducationSections >= maxEducationSections}
                sx={{
                  mt: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#ffffff",
                  '&.Mui-disabled': {
                    color: "rgba(255,255,255,0.7)",
                  },
                }}
              >
                Add Education Details
              </Button>
            </div>
          </div>

          {/* 💼 4. Work Experience Details Section */}
          <div className="mt-6 mb-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b-2 border-cyan-500 pb-2">💼 4. Work Experience Details</h3>
            <div className="space-y-6">
              {Array.from({ length: visibleWorkSections }, (_, index) => {
                const section = index + 1;
                return (
                  <div key={section} className="border-l-4 border-cyan-300 pl-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-md font-semibold text-gray-700">Work Experience {section}</h4>
                      {section > 1 && (
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          onClick={() => handleRemoveWorkSection(section)}
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            color: "#d32f2f",
                            backgroundColor: "rgba(211,47,47,0.12)",
                            '&:hover': {
                              backgroundColor: "rgba(211,47,47,0.2)",
                            },
                          }}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                    <WorkFields
                      formData={newMember}
                      handleChange={handleInputChange}
                      countries={allCountries}
                      states={allStates}
                      cities={CITIES}
                      section={section}
                      textInputStyle={{}}
                      fieldErrors={workFieldErrors}
                    />
                  </div>
                );
              })}
              
              {/* Work section error message */}
              {workSectionError && (
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                  {workSectionError}
                </Typography>
              )}

              {/* Add Work Experience button */}
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddWorkSection}
                disabled={visibleWorkSections >= maxWorkSections}
                sx={{
                  mt: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#ffffff",
                  '&.Mui-disabled': {
                    color: "rgba(255,255,255,0.7)",
                  },
                }}
              >
                Add Work Experience
              </Button>
            </div>
          </div>

          {/* 🆔 5. ID Card Details Section */}
          <div className="mt-6 mb-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b-2 border-purple-500 pb-2">🆔 5. ID Card Details (Optional)</h3>
            
            {/* Render ID card sections */}
            {Array.from({ length: visibleIDCardSections }, (_, index) => {
              const section = index + 1;
              
              return (
                <div key={section} className="border-l-4 border-purple-300 pl-4 mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-md font-semibold text-gray-700">ID Card {section}</h4>
                    {section > 1 && (
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => handleRemoveIDCardSection(section)}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          color: "#d32f2f",
                          backgroundColor: "rgba(211,47,47,0.12)",
                          '&:hover': {
                            backgroundColor: "rgba(211,47,47,0.2)",
                          },
                        }}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                  <IDCardFields
                    formData={newMember}
                    handleChange={handleInputChange}
                    section={section}
                    fieldErrors={idCardFieldErrors}
                  />
                </div>
              );
            })}

            {/* Error message and Add button */}
            <div className="mt-4 flex flex-col items-start gap-2">
              {idCardSectionError && (
                <p className="text-red-600 text-sm">{idCardSectionError}</p>
              )}
              {visibleIDCardSections < maxIDCardSections && (
                <Button
                  variant="outlined"
                  onClick={handleAddIDCardSection}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    color: "#7c3aed",
                    borderColor: "#7c3aed",
                    "&:hover": {
                      borderColor: "#6d28d9",
                      backgroundColor: "rgba(124,58,237,0.04)",
                    },
                  }}
                >
                  + Add ID Card
                </Button>
              )}
            </div>
          </div>

          {/* 🏦 6. Bank Details Section */}
          <div className="mt-6 mb-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b-2 border-green-500 pb-2">🏦 6. Bank Details (Optional)</h3>
            
            {/* Render bank sections */}
            {Array.from({ length: visibleBankSections }, (_, index) => {
              const section = index + 1;
              
              return (
                <div key={section} className="border-l-4 border-green-300 pl-4 mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-md font-semibold text-gray-700">Bank Account {section}</h4>
                    {section > 1 && (
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => handleRemoveBankSection(section)}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          color: "#d32f2f",
                          backgroundColor: "rgba(211,47,47,0.12)",
                          '&:hover': {
                            backgroundColor: "rgba(211,47,47,0.2)",
                          },
                        }}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                  <BankDetailsFields
                    formData={newMember}
                    handleChange={handleInputChange}
                    section={section}
                    fieldErrors={bankFieldErrors}
                  />
                </div>
              );
            })}

            {/* Error message and Add button */}
            <div className="mt-4 flex flex-col items-start gap-2">
              {bankSectionError && (
                <p className="text-red-600 text-sm">{bankSectionError}</p>
              )}
              {visibleBankSections < maxBankSections && (
                <Button
                  variant="outlined"
                  onClick={handleAddBankSection}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    color: "#10b981",
                    borderColor: "#10b981",
                    "&:hover": {
                      borderColor: "#059669",
                      backgroundColor: "rgba(16,185,129,0.04)",
                    },
                  }}
                >
                  + Add Bank Account
                </Button>
              )}
            </div>
          </div>

        {/* 🌐 7. Social Media Links Section */}
        <div className="mt-6 mb-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b-2 border-indigo-500 pb-2">🌐 7. Social Media Links (Optional)</h3>
          
          {/* Render social links sections */}
          {Array.from({ length: visibleSocialLinksSections }, (_, index) => {
            const section = index + 1;
            
            return (
              <div key={section} className="border-l-4 border-indigo-300 pl-4 mb-6">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-md font-semibold text-gray-700">Social Link {section}</h4>
                  {section > 1 && (
                    <Button
                      variant="contained"
                      color="error"
                      size="small"
                      onClick={() => handleRemoveSocialLinksSection(section)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        color: "#d32f2f",
                        backgroundColor: "rgba(211,47,47,0.12)",
                        '&:hover': {
                          backgroundColor: "rgba(211,47,47,0.2)",
                        },
                      }}
                    >
                      Remove
                    </Button>
                  )}
                </div>
                <SocialLinksFields
                  formData={newMember}
                  handleChange={handleInputChange}
                  section={section}
                  fieldErrors={socialLinksFieldErrors}
                />
              </div>
            );
          })}

          {/* Error message and Add button */}
          <div className="mt-4 flex flex-col items-start gap-2">
            {socialLinksSectionError && (
              <p className="text-red-600 text-sm">{socialLinksSectionError}</p>
            )}
            {visibleSocialLinksSections < maxSocialLinksSections && (
              <Button
                variant="outlined"
                onClick={handleAddSocialLinksSection}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#6366f1",
                  borderColor: "#6366f1",
                  "&:hover": {
                    borderColor: "#4f46e5",
                    backgroundColor: "rgba(99,102,241,0.04)",
                  },
                }}
              >
                + Add Social Link
              </Button>
            )}
          </div>
        </div>
        </div>
        {/* End of New Form Fields Container */}

        {/* Form Actions */}
        <div className="flex flex-col sm:flex-row justify-center sm:justify-evenly gap-3 sm:gap-4 px-2 sm:px-0">
          <Button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            variant="outlined"
            sx={{
              minWidth: { xs: '100%', sm: 140 },
              color: '#374151',
              borderColor: '#d1d5db',
              fontWeight: 600,
              textTransform: 'none',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              padding: { xs: '8px 16px', sm: '10px 20px' },
              '&:hover': {
                backgroundColor: '#f3f4f6',
                borderColor: '#c7cbd1'
              },
              '&.Mui-disabled': {
                opacity: 0.6
              }
            }}
          >
            Cancel
          </Button>

          <Button
            type="button"
            onClick={handleClear}
            disabled={isSubmitting}
            variant="contained"
            sx={{
              minWidth: { xs: '100%', sm: 140 },
              color: '#fff',
              backgroundColor: '#ef4444',
              borderColor: '#ef4444',
              fontWeight: 600,
              textTransform: 'none',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              padding: { xs: '8px 16px', sm: '10px 20px' },
              '&:hover': {
                backgroundColor: '#dc2626',
                borderColor: '#dc2626'
              },
              '&.Mui-disabled': {
                opacity: 0.6
              }
            }}
          >
            Clear
          </Button>

          <Button
            type="submit"
            disabled={isSubmitting || relationInvalid || casteInvalid || religionInvalid}
            variant="contained"
            sx={{
              minWidth: { xs: '100%', sm: 220 },
              color: '#fff',
              backgroundColor: '#2563eb',
              fontWeight: 600,
              textTransform: 'none',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              padding: { xs: '8px 16px', sm: '10px 20px' },
              '&:hover': {
                backgroundColor: '#1e40af'
              },
              '&.Mui-disabled': {
                opacity: 0.5
              }
            }}
          >
            {isSubmitting ? 'Saving...' : (mode === 'edit' ? 'Update Family Member' : 'Add Family Member')}
          </Button>
        </div>
      </form>
    </div>

      <Dialog open={cameraDialogOpen} onClose={handleCloseCamera} maxWidth="sm" fullWidth>
        <DialogTitle>
          Take Photo
          <IconButton
            aria-label="close"
            onClick={handleCloseCamera}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", padding: "20px" }}>
          {!capturedImage ? (
            <Box sx={{ position: 'relative', display: 'inline-block' }}>
              {cameraError ? (
                <Box sx={{
                  width: 400,
                  height: 300,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px dashed #ccc',
                  borderRadius: '8px',
                  backgroundColor: '#f5f5f5',
                  px: 2,
                }}>
                  <Typography variant="h6" color="error" gutterBottom>
                    Camera Error
                  </Typography>
                  <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 2 }}>
                    {cameraError}
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={retryCamera}
                    startIcon={<CameraAltIcon />}
                    sx={{
                      fontWeight: 600,
                      color: '#fff',
                      backgroundColor: '#1976d2',
                      '&:hover': {
                        backgroundColor: '#115293',
                      },
                    }}
                  >
                    Retry Camera
                  </Button>
                </Box>
              ) : webcamKey < 0 ? (
                <Box sx={{
                  width: 400,
                  height: 300,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px dashed #ccc',
                  borderRadius: '8px',
                  backgroundColor: '#f5f5f5'
                }}>
                  <Typography variant="body1" color="text.secondary">
                    {cameraMessage || 'Preparing camera...'}
                  </Typography>
                </Box>
              ) : (
                <Webcam
                  key={`webcam-${webcamKey}-${facingMode}-${selectedDeviceId || 'default'}`}
                  audio={false}
                  ref={webcamRef}
                  screenshotFormat="image/jpeg"
                  screenshotQuality={0.92}
                  width={400}
                  height={300}
                  style={{ borderRadius: "8px", objectFit: "cover" }}
                  videoConstraints={videoConstraints ?? undefined}
                  mirrored={facingMode === 'user'}
                  onUserMediaError={handleCameraError}
                  onUserMedia={handleCameraReady}
                />
              )}
              {!cameraReady && !cameraError && webcamKey >= 0 && (
                <Box sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                  zIndex: 1,
                }}>
                  {cameraMessage || 'Loading camera...'}
                </Box>
              )}
              <Box sx={{ mt: 2, display: "flex", justifyContent: "center", gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={capturePhoto}
                  disabled={!cameraReady || cameraError}
                  sx={{
                    fontWeight: 600,
                    color: '#fff',
                    backgroundColor: '#1976d2',
                    '&:hover': {
                      backgroundColor: '#115293',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: 'rgba(25, 118, 210, 0.4)',
                      color: '#fff',
                    },
                  }}
                >
                  Capture Photo
                </Button>
                <Button
                  variant="outlined"
                  onClick={switchCamera}
                  startIcon={<CameraAltIcon />}
                  disabled={!cameraReady || cameraError || videoDevices.length <= 1}
                  sx={{
                    fontWeight: 600,
                    color: '#1976d2',
                    borderColor: '#1976d2',
                    '&:hover': {
                      borderColor: '#115293',
                      color: '#115293',
                    },
                    '&.Mui-disabled': {
                      borderColor: 'rgba(25, 118, 210, 0.4)',
                      color: 'rgba(25, 118, 210, 0.4)',
                    },
                  }}
                >
                  Switch Camera
                </Button>
              </Box>
              {cameraMessage && cameraReady && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {cameraMessage}
                </Typography>
              )}
            </Box>
          ) : (
            <Box>
              <img
                src={capturedImage}
                alt="Captured"
                style={{
                  maxWidth: "100%",
                  maxHeight: "300px",
                  borderRadius: "8px",
                }}
              />
              <Box sx={{ mt: 2, display: "flex", justifyContent: "center", gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleConfirmPhoto}
                  sx={{
                    fontWeight: 600,
                    color: '#fff',
                    backgroundColor: '#1976d2',
                    '&:hover': {
                      backgroundColor: '#115293',
                    },
                  }}
                >
                  Confirm
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleRetakePhoto}
                  sx={{
                    fontWeight: 600,
                    color: '#1976d2',
                    borderColor: '#1976d2',
                    '&:hover': {
                      borderColor: '#115293',
                      color: '#115293',
                    },
                  }}
                >
                  Retake
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleCloseCamera}
                  sx={{
                    fontWeight: 600,
                    color: '#d32f2f',
                    borderColor: '#d32f2f',
                    '&:hover': {
                      borderColor: '#b71c1c',
                      color: '#b71c1c',
                    },
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <AddMasterDataDialog
        open={addDialog.open}
        onClose={closeAddMasterDialog}
        category={addDialog.category}
        initialValue={addDialog.initialValue}
        sessionId={sessionId}
        onSuccess={handleAddMasterSuccess}
      />

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={(event, reason) => {
          // Prevent closing when clicking outside or pressing ESC
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
          handleCancelConfirm();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 2,
          pt: 2.5,
        }}>
          <Typography variant="h5" component="span" fontWeight="700" sx={{ fontSize: '1.4rem' }}>
            Confirm Relationship
          </Typography>
          <IconButton
            edge="end"
            onClick={handleCancelConfirm}
            aria-label="close"
            sx={{ 
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 4, px: 3, backgroundColor: '#f8fafc' }}>
          {confirmDialog.data && (
            <Box sx={{ textAlign: 'center' }}>
              {/* Forward Relationship */}
              <Box sx={{ 
                mb: 3, 
                p: 3, 
                borderRadius: 2, 
                backgroundColor: 'white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              }}>
                <Typography variant="body1" sx={{ fontSize: '1.15rem', lineHeight: 1.8 }}>
                  <Typography component="span" sx={{ 
                    color: '#2563eb', 
                    fontWeight: 700, 
                    fontSize: '1.2rem',
                  }}>
                    {confirmDialog.data.enteredName}
                  </Typography>
                  {' '}is the{' '}
                  <Typography component="span" sx={{ 
                    color: '#059669', 
                    fontWeight: 700,
                    fontSize: '1.2rem',
                  }}>
                    {confirmDialog.data.relationshipType === 'Spouse'
                      ? ((String(newMember.gender_c || newMember.gender || '').toLowerCase() === 'male') ? 'Husband' : 'Wife')
                      : confirmDialog.data.relationshipType}
                  </Typography>
                  {' '}of{' '}
                  <Typography component="span" sx={{ 
                    color: '#dc2626', 
                    fontWeight: 700,
                    fontSize: '1.2rem',
                  }}>
                    {confirmDialog.data.selectedPersonName}
                  </Typography>
                </Typography>
              </Box>
              
              {/* Reverse Relationship */}
              <Box sx={{ 
                mb: 2, 
                p: 3, 
                borderRadius: 2, 
                backgroundColor: 'white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              }}>
                <Typography variant="body1" sx={{ fontSize: '1.15rem', lineHeight: 1.8 }}>
                  <Typography component="span" sx={{ 
                    color: '#dc2626', 
                    fontWeight: 700,
                    fontSize: '1.2rem',
                  }}>
                    {confirmDialog.data.selectedPersonName}
                  </Typography>
                  {' '}is the{' '}
                  <Typography component="span" sx={{ 
                    color: '#059669', 
                    fontWeight: 700,
                    fontSize: '1.2rem',
                  }}>
                    {confirmDialog.data.relationshipType === 'Parent'
                      ? 'Child'
                      : confirmDialog.data.relationshipType === 'Child'
                        ? 'Parent'
                        : ((String((selectableFamilyMembers.find(m => m.id === newMember.selectedPersonId)?.gender_c) || '').toLowerCase() === 'male') ? 'Husband' : 'Wife')}
                  </Typography>
                  {' '}of{' '}
                  <Typography component="span" sx={{ 
                    color: '#2563eb', 
                    fontWeight: 700,
                    fontSize: '1.2rem',
                  }}>
                    {confirmDialog.data.enteredName}
                  </Typography>
                </Typography>
              </Box>
              
              <Typography variant="body2" sx={{ 
                mt: 3, 
                color: '#64748b',
                fontSize: '0.95rem',
                fontStyle: 'italic',
              }}>
                Please confirm this relationship is correct
              </Typography>
            </Box>
          )}
        </DialogContent>
        <Box sx={{ 
          p: 2.5, 
          display: 'flex', 
          justifyContent: 'center', 
          gap: 2,
          backgroundColor: 'white',
        }}>
          <Button
            variant="outlined"
            onClick={handleCancelConfirm}
            sx={{
              fontWeight: 600,
              fontSize: '1rem',
              px: 4,
              py: 1.2,
              color: '#64748b',
              borderColor: '#cbd5e1',
              borderWidth: 2,
              textTransform: 'none',
              '&:hover': {
                borderColor: '#94a3b8',
                backgroundColor: '#f1f5f9',
                borderWidth: 2,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmSave}
            sx={{
              fontWeight: 700,
              fontSize: '1rem',
              px: 5,
              py: 1.2,
              color: '#fff',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              textTransform: 'none',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f91 100%)',
                boxShadow: '0 6px 16px rgba(102, 126, 234, 0.5)',
              },
            }}
          >
            Confirm
          </Button>
        </Box>
      </Dialog>

      {/* Success Snackbar */}
      <MDSnackbar
        color="success"
        icon="check"
        title="Success"
        content={snackbarMessage}
        dateTime="just now"
        open={successSB}
        onClose={() => setSuccessSB(false)}
        close={() => setSuccessSB(false)}
        bgWhite
      />

      {/* Error Snackbar */}
      <MDSnackbar
        color="error"
        icon="warning"
        title="Error"
        content={snackbarMessage}
        dateTime="just now"
        open={errorSB}
        onClose={() => setErrorSB(false)}
        close={() => setErrorSB(false)}
        bgWhite
      />
    </>
  );
};

export default FamilyMemberDialog;

